import { id } from "date-fns/locale";
import { API_ROUTES } from "./apiRoutes";
import { APP_ROUTES } from "./appRoutes";
import PERMISSIONS_OBJECT from "./Permissions.json";
import {
  CalendarIcon,
  ClockIcon,
  DatabaseIcon,
  FileCheckIcon,
  LocationIcon,
  MapIcon,
  MultiUserIcon,
  SettingsIcon,
} from "pages/components/V2/SvgComps";

// Payments & Business ↔ Hardware & Machine Setup gate.
// Some enabled payment methods require a peripheral to be configured in
// Hardware & Machine Setup before the section can be saved. Each entry maps a
// payment-method name to its peripheral (key + backend type), a display label,
// and a config "kind":
//   - "cardReader": full config (status + brand + channel + ≥1 instance)
//   - "simple":     status + brand only (e.g. Cash → Cash Collector)
export const PAYMENT_METHOD_PERIPHERALS = [
  {
    method: "Card Reader",
    peripheralKey: "cardReader",
    type: "card_reader",
    label: "Card Reader",
    kind: "cardReader",
  },
  {
    method: "Cash",
    peripheralKey: "billCollector",
    type: "bill_collector",
    label: "Cash Collector",
    kind: "simple",
  },
  {
    // POS payment method ↔ Point of Sale Machine peripheral. "simple" gating:
    // status Available + brand is enough. The POS Brand can be set inline in the
    // Payments & Business section (see the "payment-peripheral-brand" field) or
    // in Hardware & Machine Setup — both read/write the same peripheral.
    method: "POS",
    peripheralKey: "pointOfSaleMachine",
    type: "pos_machine",
    label: "Point of Sale Machine",
    kind: "simple",
  },
];

// Is the peripheral behind a given mapping entry fully configured?
export const isPeripheralConfigured = (peripherals = {}, entry) => {
  const p = peripherals?.[entry?.peripheralKey];
  if (!p || p.status !== "available" || !p.brand) return false;
  if (entry.kind === "cardReader") {
    // Channel + Instance are a customer-platform selection: a channel code
    // (cardChannel) + the chosen platform instance (customerPlatformId).
    return !!(p.cardChannel && p.customerPlatformId);
  }
  return true; // "simple": status Available + brand is enough
};

// Mapping entries whose payment method is currently enabled.
export const getEnabledPaymentPeripherals = (enabledPaymentMethods = []) =>
  PAYMENT_METHOD_PERIPHERALS.filter((e) =>
    (enabledPaymentMethods || []).some((m) => m?.name === e.method),
  );

// Enabled payment peripherals that still need configuration.
export const getUnconfiguredPaymentPeripherals = (
  enabledPaymentMethods = [],
  peripherals = {},
) =>
  getEnabledPaymentPeripherals(enabledPaymentMethods).filter(
    (e) => !isPeripheralConfigured(peripherals, e),
  );

// Evaluate a section's `disableAllWhen` rule against formData. When true, every
// non-exempt field/component in the section is greyed out and its validation is
// skipped on save. Matching mirrors the field-level conditionals:
//   { field }                       → truthy value
//   { field, equals }               → strict equality on the raw value
//   { field, equalsName }           → the selected option object's `name`
// The controlling field may live in another section (formData is shared), e.g.
// Display & Content is gated on Hardware & Machine Setup's Display Type.
export const isSectionDisabledByRule = (rule, formData = {}) => {
  if (!rule || !rule.field) return false;
  const dep = formData[rule.field];
  if (rule.equalsName !== undefined) {
    const depName = dep && typeof dep === "object" ? dep.name : dep;
    return depName === rule.equalsName;
  }
  if (rule.equals !== undefined) {
    return dep === rule.equals;
  }
  return !!dep;
};

// Placeholder options for the Refill Configuration external-storage
// Channel / Instance dropdowns. Static for now; only the selected id is
// persisted (storage_channel_id / storage_instance_id).
export const STORAGE_CHANNEL_OPTIONS = [{ id: 1, name: "Retailfy" }];
export const STORAGE_INSTANCE_OPTIONS = [
  { id: 1, name: "JTAP" },
  { id: 2, name: "VB" },
  { id: 3, name: "BF" },
  { id: 4, name: "JT" },
  { id: 5, name: "BO" },
  { id: 6, name: "Radium" },
];

// NOTE: Display & Content dropdown option lists now come from the backend
// (list.master_config_dropdowns → displayContentOptions), loaded into
// listOfMasterType by field label. The GET resolves stored codes to option
// objects server-side, so no static option arrays live here anymore.

export const ENV_CONFIG = {
  baseURL: process.env.REACT_APP_API_ENDPOINT,
  refillURL: process.env.REACT_APP_REFILL_API_ENDPOINT,
  analyticsBaseURL: process.env.REACT_APP_API_ENDPOINT_ANALYTICS,
  eventApiURL: process.env.REACT_APP_EVENT_API_ENDPOINT,
  googleMapURL: `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_API_KEY}&v=3.exp&libraries=geometry,drawing,places`,
  notificationWebSocketUrl: process.env.REACT_APP_NOTIFICATIONS_WEB_SOCKET_URL,
  vmConnectionWebSocketUrl: process.env.REACT_APP_VM_CONNECTION_WEB_SOCKET_URL,
  bulkStockSampleFileUrl: process.env.REACT_APP_BULK_STOCK_SAMPLE_FILE,
  sentryApiKey: process.env.REACT_APP_SENTRY_KEY,
  googleApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
};

export const BROWSER_STORAGE = {
  ACCESS_TOKEN: "access_token",
  USERS_DETAILS: "user_details",
  USER_PERMISSIONS: "user_permissions",
  COOKIE_EXPIRY_TIME: 60 * 60 * 23,
  TOKEN_EXPIRY_TIME: null,
};

export const NAVBAR_BRAND_ROUTES = {
  "Machine Operations Center": [APP_ROUTES.MACHINE_EVENT_LOG],
  "Machine Communication Log": [APP_ROUTES.MACHINE_EVENT_LOG],
  "Report Catalog": ["/reports-list"],
  "Generate Report": ["/generate-report"],
  "Vending Machines": ["/system-auditor-vending-machine"],
  "Refill Trigger": ["/refill-trigger"],
  "Recurring Reports": ["/recurring-reports"],
  "Incident Trigger": ["/incident-trigger"],
  "Planogram Adherence": ["/planogram-adherence"],
  "System Auditor": [APP_ROUTES.MASTER_DATA_SETUP],
  "Master Data Setup": [APP_ROUTES.MASTER_DATA_SETUP],
  "Dashboard Management": ["/dashboard", APP_ROUTES.VM_ANALYTICS],
  Dashboard: ["/dashboard"],
  "VM Analytics": [APP_ROUTES.VM_ANALYTICS],
  "Role Management": ["/roles", "/role", "/add-role", "/edit-role"],
  "User List": [
    "/users",
    "/user",
    "/add-user",
    "/edit-user",
    APP_ROUTES.CREATE_WAREHOUSE_MANAGER,
    APP_ROUTES.EDIT_WAREHOUSE_MANAGER,
    APP_ROUTES.VIEW_WAREHOUSE_MANAGER,
  ],
  "User Management": [
    "/users",
    "/user",
    "/add-user",
    "/edit-user",
    "/delivery-partners",
    "/add-delivery-partner",
    "/edit-delivery-partner",
    "/delivery-partner",
    "/refill-partners",
    "/refill-partner",
    "/add-refill-partner",
    "/edit-refill-partner",
    APP_ROUTES.LINKED_NEW_VM,
    APP_ROUTES.EDIT_LINKED_VM,
    APP_ROUTES.LINKED_NEW_WAREHOUSE,
    APP_ROUTES.EDIT_LINKED_WAREHOUSE,
    APP_ROUTES.LINKED_NEW_VEHICLE,
    APP_ROUTES.EDIT_LINKED_VEHICLE,
    APP_ROUTES.CREATE_WAREHOUSE_MANAGER,
    APP_ROUTES.EDIT_WAREHOUSE_MANAGER,
    APP_ROUTES.VIEW_WAREHOUSE_MANAGER,
    APP_ROUTES.LINKED_NEW_WAREHOUSE_FOR_WM,
    APP_ROUTES.EDIT_LINKED_WAREHOUSE_FOR_WM,
  ],
  "Product Catalog": [
    "/products",
    "/product",
    "/add-product",
    "/edit-product",
    "/sync-products",
    "/new-product-list",
  ],
  "Product List": ["/products"],
  "Product Category": ["/product-categories", "/product-category"],
  "Product Brand": ["/product-brands", "/product-brand"],
  "Product Unit": ["/product-Units", "/product-unit"],
  "Product Packaging": ["/product-packagings", "/product-packaging"],
  "Sync Product List": ["/sync-products"],
  "New Product List": ["/new-product-list"],
  Brands: ["/sync-products"],
  "Franchise Management": [
    "/franchises",
    "/add-franchise",
    "/franchise",
    "/edit-franchise",
  ],
  Entity: [
    "/franchises",
    "/add-franchise",
    "/franchise",
    "/edit-franchise",
    APP_ROUTES.RETAILERS,
    "/zones",
    "/add-zone",
    "/edit-zone",
    "/zone",
    "/stores",
    "/store-inventory",
  ],
  "Inventory Management": ["/inventory"],
  "Warehouse Management": [
    "/warehouses",
    "/add-warehouse",
    "/warehouse",
    APP_ROUTES.EDIT_WAREHOUSE,
    APP_ROUTES.SUPPLIER_LIST,
    APP_ROUTES.SUPPLIER_ADD,
    APP_ROUTES.SUPPLIER_VIEW,
    APP_ROUTES.SUPPLIER_EDIT,
    APP_ROUTES.PURCHASE_ORDER_LIST,
    APP_ROUTES.PURCHASE_ORDER_ADD,
    APP_ROUTES.PURCHASE_ORDER_EDIT,
    APP_ROUTES.SAME_PURCHASE_ORDER,
    APP_ROUTES.PURCHASE_ORDER_MANAGE,
    APP_ROUTES.PURCHASE_ORDER_VIEW,
    APP_ROUTES.WAREHOUSE_ORDER_LIST,
    APP_ROUTES.SUPPLIER_RETURN_OVERVIEW,
    APP_ROUTES.GENERATE_SHIPPING_ORDER_REQUEST,
    "/shipping",
    APP_ROUTES.CREATE_SHIPPING_ORDER,
    APP_ROUTES.MANAGE_SHIPPING_ORDER,
    APP_ROUTES.VIEW_SHIPPING_ORDER,
    APP_ROUTES.TALLY_INVENTORY_OVERVIEW,
    APP_ROUTES.TALLY_INVENTORY_ADD,
    APP_ROUTES.LINKED_NEW_REFILL_PARTNER,
    APP_ROUTES.EDIT_LINKED_REFILL_PARTNER,
    APP_ROUTES.LINKED_NEW_WAREHOUSE_MANAGER,
    APP_ROUTES.EDIT_LINKED_WAREHOUSE_MANAGER,
    APP_ROUTES.VIEW_PACKAGE_TRANSFER,
    APP_ROUTES.VIEW_KIT_TRANSFER,
    APP_ROUTES.CREATE_KIT_TRANSFER,
    APP_ROUTES.CREATE_PACKAGE_TRANSFER,
    APP_ROUTES.VIEW_REQUEST_ORDER,
    APP_ROUTES.VIEW_CASH_COLLECTION,
    APP_ROUTES.VIEW_SALE_ORDER,
    APP_ROUTES.PURCHASE_RETURN_ADD,
    APP_ROUTES.PURCHASE_ORDER_RETURN,
    APP_ROUTES.PURCHASE_ORDER_EDIT,
  ],
  "Warehouse List": [
    "/warehouses",
    "/add-warehouse",
    "/warehouse",
    APP_ROUTES.EDIT_WAREHOUSE,
    APP_ROUTES.SUPPLIER_LIST,
    APP_ROUTES.SUPPLIER_ADD,
    APP_ROUTES.SUPPLIER_VIEW,
    APP_ROUTES.SUPPLIER_EDIT,
    APP_ROUTES.PURCHASE_ORDER_LIST,
    APP_ROUTES.PURCHASE_ORDER_ADD,
    APP_ROUTES.PURCHASE_ORDER_EDIT,
    APP_ROUTES.SAME_PURCHASE_ORDER,
    APP_ROUTES.PURCHASE_ORDER_MANAGE,
    APP_ROUTES.PURCHASE_ORDER_VIEW,
    APP_ROUTES.WAREHOUSE_ORDER_LIST,
    APP_ROUTES.SUPPLIER_RETURN_OVERVIEW,
    APP_ROUTES.GENERATE_SHIPPING_ORDER_REQUEST,
    "/shipping",
    APP_ROUTES.CREATE_SHIPPING_ORDER,
    APP_ROUTES.MANAGE_SHIPPING_ORDER,
    APP_ROUTES.VIEW_SHIPPING_ORDER,
    APP_ROUTES.TALLY_INVENTORY_OVERVIEW,
    APP_ROUTES.TALLY_INVENTORY_ADD,
  ],
  "Delivery Partner": [
    "/delivery-partners",
    "/add-delivery-partner",
    "/edit-delivery-partner",
    "/delivery-partner",
  ],
  "Zone Management": ["/zones", "/add-zone", "/edit-zone", "/zone"],
  "Delivery Person": [
    "/delivery-persons",
    "/delivery-person",
    "/add-delivery-person",
    "/edit-delivery-person",
  ],
  "Refill Partner": [
    "/refill-partners",
    "/refill-partner",
    "/add-refill-partner",
    "/edit-refill-partner",
    APP_ROUTES.LINKED_NEW_VM,
    APP_ROUTES.EDIT_LINKED_VM,
    APP_ROUTES.LINKED_NEW_WAREHOUSE,
    APP_ROUTES.EDIT_LINKED_WAREHOUSE,
    APP_ROUTES.LINK_REFILL_PERMISSION,
    APP_ROUTES.EDIT_LINK_REFILL_PERMISSION,
    APP_ROUTES.LINK_REFILL_NOTIFICATIONS,
    APP_ROUTES.EDIT_LINK_REFILL_NOTIFICATIONS,
  ],
  "Technician VM List": [
    APP_ROUTES.TECHNICIAN_VM_LIST,
    "/tech-vm-snapshot",
    "/tech-franchise",
    APP_ROUTES.TECH_EDIT_VENDING_MACHINE,
    APP_ROUTES.TECH_CREATE_VENDING_MACHINE,
  ],
  "Technician Management": [
   // APP_ROUTES.TECHNICIAN_TRANSACTION_LIST,
    APP_ROUTES.GET_INCIDENT_LIST,
    APP_ROUTES.TECHNICIAN_PARTNER_LIST,
    APP_ROUTES.GET_JOB_LIST,
    APP_ROUTES.TECHNICIAN_LOGS,
    APP_ROUTES.MOTOR_ACTION_LOGS,
    APP_ROUTES.TECH_VM_TEMPLATE_LIST,
    APP_ROUTES.TECH_VM_TEMPLATE_ADD,
    APP_ROUTES.TECH_VM_TEMPLATE_EDIT,
    APP_ROUTES.TECH_VM_TEMPLATE_CLONE,
    // APP_ROUTES.VM_TEMPLATE_LIST,
  ],

  "VM Template List": [
    APP_ROUTES.TECH_VM_TEMPLATE_LIST,
    APP_ROUTES.TECH_VM_TEMPLATE_ADD,
    APP_ROUTES.TECH_VM_TEMPLATE_EDIT,
    APP_ROUTES.TECH_VM_TEMPLATE_CLONE,
  ],

  
 
  "Incident List": [
    API_ROUTES.GET_INCIDENT_LIST,
  ],
  "Job List": [
    API_ROUTES.GET_JOB_LIST,
  ],
  "Technician Logs": [
    APP_ROUTES.TECHNICIAN_LOGS,
  ],
  "Motor Action Logs": [
    APP_ROUTES.MOTOR_ACTION_LOGS
  ],
  "Store Management": [
    "/stores",
    "/store-inventory",
    // APP_ROUTES.GENERATE_SHIPPING_ORDER_REQUEST,
    // // APP_ROUTES.STORE_MANAGER_LIST,
    // // APP_ROUTES.STORE_MANAGER_ADD,
    // // APP_ROUTES.STORE_MANAGER_EDIT,
    // // APP_ROUTES.STORE_MANAGER_VIEW,
    // "/shippings",
    // "/shipping",
    // "/add-shipping",
    // APP_ROUTES.MANAGE_SHIPPING_ORDER,
  ],
  // "Store Manager": [
  //   APP_ROUTES.STORE_MANAGER_LIST,
  //   APP_ROUTES.STORE_MANAGER_ADD,
  //   APP_ROUTES.STORE_MANAGER_EDIT,
  //   APP_ROUTES.STORE_MANAGER_VIEW,
  // ],
  // "Refill Management": [APP_ROUTES.LIST_REFILLS, APP_ROUTES.ADD_REFILL_PLAN],
  "Refill Plan": [
    APP_ROUTES.LIST_REFILLS,
    APP_ROUTES.ADD_REFILL_PLAN,
    APP_ROUTES.VIEW_TALLY_ORDER_OVERVIEW,
    APP_ROUTES.VIEW_CASH_ORDER_OVERVIEW,
    APP_ROUTES.VIEW_REFILL_PLAN,
    APP_ROUTES.RETURN_ORDER_VIEW,
    APP_ROUTES.EDIT_REFILL_PLAN,
    APP_ROUTES.VIEW_TOPUP_OVERVIEW,
  ],

  "Vending Machine": [
    "/vm",
    APP_ROUTES.CREATE_VENDING_MACHINE,
    APP_ROUTES.EDIT_VENDING_MACHINE,
    "/vm-snapshot",
    // APP_ROUTES.LIST_REFILLS,
    // APP_ROUTES.ADD_REFILL_PLAN,
    // APP_ROUTES.EDIT_REFILL_PLAN,
    // APP_ROUTES.VIEW_REFILL_PLAN,
    APP_ROUTES.VM_TEMPLATE_LIST,
    APP_ROUTES.VM_TEMPLATE_LIST_V2,
    APP_ROUTES.VM_TEMPLATE_ADD,
    APP_ROUTES.VM_TEMPLATE_EDIT,
    APP_ROUTES.VM_TEMPLATE_CLONE,
    APP_ROUTES.BULK_REFILL_LIST,
    APP_ROUTES.BULK_REFILL_ADD,
    APP_ROUTES.BULK_REFILL_EDIT,
    APP_ROUTES.BULK_REFILL_VIEW,
    APP_ROUTES.VM_SETTINGS_LIST,
    APP_ROUTES.VM_SETTINGS_ADD,
    APP_ROUTES.VM_SETTINGS_EDIT,
    APP_ROUTES.VM_SETTINGS_CLONE,
    APP_ROUTES.VM_SETTINGS_VIEW,
    APP_ROUTES.CHANGE_PLANOGRAM_FOR_MULTIPLE_VMs,
    APP_ROUTES.PLANOGRAM_TEMPLATE_ADD,
    APP_ROUTES.PLANOGRAM_TEMPLATE_CLONE,
    APP_ROUTES.PLANOGRAM_TEMPLATE_EDIT,
    APP_ROUTES.PLANOGRAM_TEMPLATE_LIST,
    APP_ROUTES.PLANOGRAM_TEMPLATE_VIEW,
  ],
  "VM List": [
    "/vm",
    APP_ROUTES.CHANGE_PLANOGRAM_FOR_MULTIPLE_VMs,
    APP_ROUTES.CREATE_VENDING_MACHINE,
    APP_ROUTES.EDIT_VENDING_MACHINE,
    "/vm-snapshot",
  ],

  "VM Templates": [
    APP_ROUTES.VM_TEMPLATE_LIST,
    APP_ROUTES.VM_TEMPLATE_ADD,
    APP_ROUTES.VM_TEMPLATE_EDIT,
    APP_ROUTES.VM_TEMPLATE_CLONE,
  ],
  "Planogram Template": [
    APP_ROUTES.PLANOGRAM_TEMPLATE_LIST,
    APP_ROUTES.PLANOGRAM_TEMPLATE_ADD,
    APP_ROUTES.PLANOGRAM_TEMPLATE_CLONE,
    APP_ROUTES.PLANOGRAM_TEMPLATE_EDIT,
    APP_ROUTES.PLANOGRAM_TEMPLATE_LIST,
    APP_ROUTES.PLANOGRAM_TEMPLATE_VIEW,
  ],
  "Bulk Refill": [
    APP_ROUTES.BULK_REFILL_LIST,
    APP_ROUTES.BULK_REFILL_ADD,
    APP_ROUTES.BULK_REFILL_EDIT,
    APP_ROUTES.BULK_REFILL_VIEW,
  ],
  "VM Board Setting Template": [
    APP_ROUTES.VM_SETTINGS_LIST,
    APP_ROUTES.VM_SETTINGS_ADD,
    APP_ROUTES.VM_SETTINGS_EDIT,
    APP_ROUTES.VM_SETTINGS_CLONE,
    APP_ROUTES.VM_SETTINGS_VIEW,
  ],
  "Shipping Management": [
    "/shippings",
    "/shipping",
    "/add-shipping",
    "/manage-shipping",
  ],
  Transactions: [
    "/orders",
    "/order",
    "/direct-sale",
    // "/sale-order",
    "/pg-module",
    APP_ROUTES.CUSTOMER_FEEDBACK_LIST,
    APP_ROUTES.REQUEST_PRODUCTS_LIST,
    APP_ROUTES.ADD_REQUEST_PRODUCT,
  ],
  "Direct Sales": ["/direct-sale"],
  "PG Module": ["/pg-module"],
  "Requested Products": [
    APP_ROUTES.REQUEST_PRODUCTS_LIST,
    APP_ROUTES.EDIT_REQUEST_PRODUCT,
  ],
  "Transaction History": ["/orders", "/order"],
  "Customer Feedback": [APP_ROUTES.CUSTOMER_FEEDBACK_LIST],
  // "Requested Products": [APP_ROUTES.REQUEST_PRODUCTS_LIST],
  "Sales Analytics": [APP_ROUTES.SALES_ANALYTICS],
  Campaigns: [
    "/offerlist/coupons",
    "/offerlist/discounts",
    "/add-coupon",
    "/edit-coupon",
    "/discount-view",
    "/edit-discount",
    "/add-discount",
    "/coupon",
    APP_ROUTES.CLONE_DISCOUNT,
    APP_ROUTES.RESTART_DISCOUNT,
    APP_ROUTES.VIEW_SUBOFFER_OVERVIEW,
    APP_ROUTES.VIEW_OFFER_HISTORY_OVERVIEW,
    APP_ROUTES.VIEW_SUBOFFER_HISTORY_OVERVIEW,
    APP_ROUTES.NEGATIVE_ITEMS_LIST,
    APP_ROUTES.ADD_NEGATIVE_ITEM,
    APP_ROUTES.EDIT_NEGATIVE_ITEM,
    APP_ROUTES.VIEW_NEGATIVE_ITEM,
    APP_ROUTES.LINKED_NEW_ITEM,
    APP_ROUTES.EDIT_LINK_ITEM,
    APP_ROUTES.LINKED_NEW_VM_NEGATIVE_ITEM,
    APP_ROUTES.EDIT_LINK_VM_NEGATIVE_ITEM,
  ],
  Offers: [
    "/offerlist/coupons",
    "/offerlist/discounts",
    "/add-coupon",
    "/edit-coupon",
    "/discount-view",
    "/edit-discount",
    "/add-discount",
    "/coupon",
    APP_ROUTES.CLONE_DISCOUNT,
    APP_ROUTES.RESTART_DISCOUNT,
    APP_ROUTES.VIEW_SUBOFFER_OVERVIEW,
    APP_ROUTES.VIEW_OFFER_HISTORY_OVERVIEW,
    APP_ROUTES.VIEW_SUBOFFER_HISTORY_OVERVIEW,
  ],
  "Supplier Management": [
    APP_ROUTES.SUPPLIER_LIST,
    APP_ROUTES.SUPPLIER_ADD,
    APP_ROUTES.SUPPLIER_VIEW,
    APP_ROUTES.SUPPLIER_EDIT,
  ],
  "Procurement Management": [
    APP_ROUTES.PURCHASE_ORDER_LIST,
    APP_ROUTES.PURCHASE_ORDER_ADD,
    APP_ROUTES.PURCHASE_ORDER_EDIT,
    APP_ROUTES.PURCHASE_ORDER_MANAGE,
    APP_ROUTES.PURCHASE_ORDER_VIEW,
    APP_ROUTES.PURCHASE_RETURN_ADD,
    APP_ROUTES.PURCHASE_ORDER_RETURN,
    APP_ROUTES.PURCHASE_ORDER_EDIT,
  ],
  "Warehouse Order": [
    APP_ROUTES.WAREHOUSE_ORDER_LIST,
    APP_ROUTES.SUPPLIER_RETURN_OVERVIEW,
    APP_ROUTES.SUPPLIER_RETURN_OVERVIEW,
    APP_ROUTES.VIEW_PACKAGE_TRANSFER,
    APP_ROUTES.VIEW_KIT_TRANSFER,
    APP_ROUTES.CREATE_KIT_TRANSFER,
    APP_ROUTES.CREATE_PACKAGE_TRANSFER,
    APP_ROUTES.VIEW_REQUEST_ORDER,
    APP_ROUTES.VIEW_CASH_COLLECTION,
    APP_ROUTES.VIEW_SALE_ORDER,
  ],
  Report: [APP_ROUTES.REPORT],
  Retailers: [APP_ROUTES.RETAILERS],
  Advertisements: [
    APP_ROUTES.ADVERTISEMENT_MANAGEMENT,
    APP_ROUTES.ADD_ADVERTISEMENT,
    APP_ROUTES.EDIT_ADVERTISEMENT,
    APP_ROUTES.VIEW_ADVERTISEMENT,
  ],
  "Ext. Sale Management": [
    APP_ROUTES.THIRD_PARTY_SALES_LIST,
    APP_ROUTES.PLATFORM_LINKED_VM_LIST,
    APP_ROUTES.VM_RESTRICTED_PRODUCT_LIST,
  ],
  "Ext. Sale Partner": [
    APP_ROUTES.THIRD_PARTY_SALES_LIST,
    APP_ROUTES.PLATFORM_LINKED_VM_LIST,
    APP_ROUTES.VM_RESTRICTED_PRODUCT_LIST,
  ],
  "Negative Items": [
    APP_ROUTES.NEGATIVE_ITEMS_LIST,
    APP_ROUTES.ADD_NEGATIVE_ITEM,
    APP_ROUTES.EDIT_NEGATIVE_ITEM,
    APP_ROUTES.VIEW_NEGATIVE_ITEM,
    APP_ROUTES.LINKED_NEW_ITEM,
    APP_ROUTES.EDIT_LINK_ITEM,
    APP_ROUTES.LINKED_NEW_VM_NEGATIVE_ITEM,
    APP_ROUTES.EDIT_LINK_VM_NEGATIVE_ITEM,
  ],
  "Technician Partner": [
    "/add-technician-partner",
    APP_ROUTES.TECHNICIAN_PARTNER_LIST,
  ],
  "Technician Partner list": [
    "/add-technician-partner",
    APP_ROUTES.TECHNICIAN_PARTNER_LIST,
  ],
  // "Action History": [
  //   "/action-history"
  // ]
};

export const DEFAULT_ERROR_MESSAGE = "Error occurred, please try again!";
export const DEFAULT_SUCCESS_MESSAGE = "Action was successful!";

export const PERMISSIONS = PERMISSIONS_OBJECT;

export const PERMISSIONS_GROUP_KEYS = {
  USER_MANAGEMENT: "User Management",
  ROLE_MANAGEMENT: "Role Management",
  PRODUCT_CATALOG: "Product Catalog",
  PRODUCT_CATEGORY: "Product Category",
  PRODUCT_BRAND: "Product Brand",
  PRODUCT_UNIT: "Product Unit",
  PRODUCT_PACKAGING: "Product Packaging",
};

export const PERMISSIONS_GROUP = {
  [PERMISSIONS_GROUP_KEYS.USER_MANAGEMENT]: {
    [PERMISSIONS.PERM_ADD_USER]: false,
    [PERMISSIONS.PERM_UPDATE_USER]: false,
    [PERMISSIONS.PERM_VIEW_USER]: false,
    [PERMISSIONS.PERM_DELETE_USER]: false,
  },
  [PERMISSIONS_GROUP_KEYS.ROLE_MANAGEMENT]: {
    [PERMISSIONS.PERM_ADD_ROLE]: false,
    [PERMISSIONS.PERM_UPDATE_ROLE]: false,
    [PERMISSIONS.PERM_VIEW_ROLE]: false,
    [PERMISSIONS.PERM_DELETE_ROLE]: false,
  },
  [PERMISSIONS_GROUP_KEYS.PRODUCT_CATALOG]: {
    [PERMISSIONS.PERM_ADD_PRODUCT_CATALOG]: false,
    [PERMISSIONS.PERM_UPDATE_PRODUCT_CATALOG]: false,
    [PERMISSIONS.PERM_VIEW_PRODUCT_CATALOG]: false,
    [PERMISSIONS.PERM_DELETE_PRODUCT_CATALOG]: false,
  },
  [PERMISSIONS_GROUP_KEYS.PRODUCT_CATEGORY]: {
    [PERMISSIONS.PERM_ADD_PRODUCT_CATEGORY]: false,
    [PERMISSIONS.PERM_UPDATE_PRODUCT_CATEGORY]: false,
    [PERMISSIONS.PERM_VIEW_PRODUCT_CATEGORY]: false,
    [PERMISSIONS.PERM_DELETE_PRODUCT_CATEGORY]: false,
  },
  [PERMISSIONS_GROUP_KEYS.PRODUCT_BRAND]: {
    [PERMISSIONS.PERM_ADD_BRAND]: false,
    [PERMISSIONS.PERM_UPDATE_BRAND]: false,
    [PERMISSIONS.PERM_VIEW_BRAND]: false,
    [PERMISSIONS.PERM_DELETE_BRAND]: false,
  },
  [PERMISSIONS_GROUP_KEYS.PRODUCT_UNIT]: {
    [PERMISSIONS.PERM_ADD_PRODUCT_UNIT]: false,
    [PERMISSIONS.PERM_UPDATE_PRODUCT_UNIT]: false,
    [PERMISSIONS.PERM_VIEW_PRODUCT_UNIT]: false,
    [PERMISSIONS.PERM_DELETE_PRODUCT_UNIT]: false,
  },
  [PERMISSIONS_GROUP_KEYS.PRODUCT_PACKAGING]: {
    [PERMISSIONS.PERM_ADD_PACKAGING]: false,
    [PERMISSIONS.PERM_UPDATE_PACKAGING]: false,
    [PERMISSIONS.PERM_VIEW_PACKAGING]: false,
    [PERMISSIONS.PERM_DELETE_PACKAGING]: false,
  },
};

export const CURRENCIES = {
  RUPEES: "₹",
};
export const RUPEE_NOTE = {
  ten: 10,
  twenty: 20,
  fifty: 50,
  hundred: 100,
  two_hundred: 200,
  five_hundred: 500,
  two_thousand: 2000,
};

export const CASH_ORDER_STATUS_CODE = {
  COLLECTED: 1,
  COUNTED: 2,
  RECEIVED: 3,
  SKIP_COUNTING: 4,
};

export const UPTIME_TYPE_OPTIONS = [
  { label: "24x7", value: "24x7" },
  { label: "Custom", value: "Custom" },
];
export const TABLES_REF = {
  VM_TEMPLATE_LIST_V2: [
    "name",
    "containers_count",
    "planograms_count",
    "linked_vm_count",
    "entity_type",
    "entity_name",
    "status",
    "last_updated",
    "action",
  ],
  FEEDBACK_TABLE: ["rating", "feedback", "device", "phone_number"],
  TALLY_INVENTORY: [
    "sku",
    "name",
    "category",
    "price",
    "last_updated",
    "old_stock",
    "new_stock",
    "adjusted",
  ],
  DISCARDED_BIN: [
    "sku",
    "name",
    "category",
    "price",
    "status",
    "old_stock",
    "stock_discarded",
  ],
  MASTER_CONFIG_ACTION_HISTORY_TABLE: [
    "version",
    "section",
    "field_updated",
    "old_value",
    "new_value",
    "updated_by",
    "timestamp",
  ],
  PRODUCT_TABLE: [
    "star",
    "sku",
    "name",
    "brand",
    "category",
    "price",
    "created_at",
    "status",
    // "rating",
    // "updated_at",
    // "stock",
    "action",
  ],
  PRODUCT_REQUESTED_TABLE: [
    "order_id",
    "name",
    "vm",
    "channel",
    "requested_by",
    "received_by",
    "action",
  ],
  USERS_TABLE: [
    "id",
    "name",
    "mobile",
    "email",
    "role",
    "entity",
    "entity_name",
    "linked_warehouse",
    "status",
    "updated_at",
    "action",
  ],

  MASTER_DATA_SETUP: [
    "module",
    "master_type",
    "master_name",
    "created_by",
    "status",
    "Actions",
  ],

  GENERATED_REPORTS: [
    "time_period",
    "vm_name",
    "type_of_report",
    "scope_type",
    "status",
    "Actions",
  ],
  ROLES_TABLE: ["name", "description", "updated_at", "action"],
  BRANDS_TABLE: ["name", "created_at", "updated_at", "action"],
  SYNC_PRODUCT_LIST_TABLE: [
    "name",
    "industry",
    "sub_industry",
    "catalogue",
    "ds",
    "ds1",
    "action",
  ],
  SYNC_BRAND_CATALOGUES_TABLE: [
    "name",
    "ds_products",
    "last_synced",
    "action",
  ],
  SYNC_CATALOGUE_PRODUCTS_TABLE: [
    "ds_code",
    "product_name",
    "category",
    "sub_category",
    "qty",
    "price",
    "packaging",
    "total_ds1",
    "synced_ds1",
    "new_ds1",
    "last_synced",
    "status",
    "action",
  ],
  NEW_PRODUCT_LIST_TABLE: [
    "ds_code",
    "product_name",
    "brand",
    "sub_brand",
    "category",
    "sub_category",
    "qty",
    "price",
    "packaging",
    "ds",
    "state",
    "action",
  ],
  TALLY_ORDER_MODAL: [
    "sku",
    "name",
    "price",
    "actual_Qty",
    "adjusted_Qty",
    "status",
  ],
  WAREHOUSE_TABLE: [
    "name",
    "type",
    "entity",
    "entity_name",
    "manager",
    "current_stock",
    "received",
    "dispatched",
    "incoming",
    "outgoing",
    "manage",
    "action",
  ],
  INCIDENT_LIST: [
    "incidentId",
    "jobId",
    "incident_type",
    "priority",
    "machine_id",
    "reported_by",
    "status",
    "created_on",
    "last_updated",
  ],
  INCIDENT_TYPE_LIST: ["incident_type", "priority", "planned_resolution"],
  ADD_INCIDENT_TYPE_LIST: [
    "incident_type",
    "priority",
    "planned_resolution",
    "action",
  ],
  JOB_LIST: [
    "jobId",
    "incidentId",
    "incident_type",
    "machine_id",
    "job_status",
    "total_tasks",
    "total_completed",
    "created_at",
    "updated_at",
  ],
  TECHNICIAN_LOGS: ["vmName", "action", "source", "sourceName", "timeStamp"],
  MOTOR_ACTION_LOGS: [
    "transactionId",
    "vmName",
    "coil",
    "container",
    "row",
    "column",
    "action",
    "status",
    "command",
    "source",
    "sourceName",
    "timeStamp",
  ],
  WAREHOUSE_ORDERS_TABLE: [
    "type",
    "order_id",
    "date_updated",
    "status",
    "source",
    "destination",
    "user",
    "date_created",
    "action",
  ],
  UNITS_TABLE: ["name", "short_code", "updated_at", "action"],
  PACKAGING_TABLE: ["name", "height", "width", "depth", "updated_at", "action"],
  CATEGORY_TABLE: ["name", "created_at", "updated_at", "action"],
  DELIVERY_PARTNER_TABLE: ["name", "mobile", "status", "updated_at", "action"],
  FRANCHISE_TABLE: [
    "name",
    "store_count",
    "vm_count",
    "state_count",
    "city_count",
    "updated_at",
    "manageStore",
    "action",
  ],
  INVENTORY_TABLE: [
    "sku",
    "name",
    "category",
    "stock",
    "status",
    "price",
    "updated_at",
  ],
  ZONE_TABLE: ["name", "warehouses", "created_at", "updated_at", "action"],
  STORE_TABLE: [
    "name",
    "linked_sms",
    "vm_count",
    "franchise",
    "address",
    "updated_at",
    "manage",
    "action",
  ],
  SHIPPING_TABLE: [
    "shipping_order_id",
    "shipping_order_status",
    "shipping_amount",
    "created_at",
    "updated_at",
    "action",
  ],
  ADD_SHIPPING_STEP2_TABLE: ["name", "address", "updated_at", "action"],
  ADD_SHIPPING_STEP3_TABLE: [
    "select_all_products",
    "sku",
    "name",
    "category",
    "price",
    "stock",
  ],
  ADD_SHIPPING_STEP4_TABLE: [
    "sku",
    "name",
    "category",
    "price",
    "stock",
    "quantity_field",
    "action",
  ],
  ADD_SHIPPING_STEP5_TABLE: [
    "name",
    "category",
    "price",
    "added_items",
    "total_cost",
  ],
  ADD_WAREHOUSE_INVENTORY_STEP1_TABLE: [
    "checkbox",
    "sku",
    "name",
    "category",
    "stock_field",
    "price",
    "updated_at",
  ],
  ORDER_REFUND_TABLE: ["sku", "name", "quantity", "amount", "reason"],
  ORDER_RECEIPT_TABLE: [
    "sku",
    "hsn",
    "name",
    "status",
    "mrp",
    "quantity",
    "coil",
    "gst",
    "cess_percentage",
    "rate",
    "discount",
    "taxable_amount",
    "gst_amount",
    "cess",
    "amount",
    "refund_amount",
    "net_amount",
  ],
  ADD_WAREHOUSE_INVENTORY_STEP2_TABLE: [
    "sku",
    "name",
    "category",
    "price",
    "stock_field",
    "quantity_field",
    "action",
  ],
  ADD_WAREHOUSE_INVENTORY_STEP3_TABLE: [
    "sku",
    "name",
    "category",
    "price",
    "stock_field",
    "items_balance",
    "updated_stock_field",
  ],
  SHIPPING_VIEW_PRODUCT_TABLE: [
    "sku",
    "name",
    "category",
    "price",
    "stock",
    // "received_stock",
    // "total_cost",
  ],
  DELIVERY_PERSON_TABLE: [
    "name",
    "franchise",
    "mobile",
    "status",
    "refill_assigned",
    "updated_at",
    "action",
  ],
  REFILL_PARTNER_TABLE: [
    "refiller_id",
    "name",
    "mobile",
    "entity",
    "entity_name",
    "vms",
    "bag",
    "success",
    "score",
    "cash",
    "status",
    "action",
  ],
  TECHNICIAN_PARTNER_LIST: [
    "technician_id",
    "name",
    "Contact No.",
    "entity",
    "entity_name",
    "vms",
    "bag",
    // "status",
    "action",
  ],
  TECHNICIAN_TRANSACTION_LIST: [
    "technician_name",
    "transaction_id",
    "vending_machine_name",
    "action_type",
    "action_payload",
    "status",
    "error_message",
    "created_at",
    "updated_at",
  ],

  REFILL_PLAN_TABLE: ["name", "state", "executed_by", "status", "updated_at"],
  CONFIGURED_VENDING_MACHINE_TABLE: [
    "name",
    "state",
    "live_stock",
    "success",
    "sale",
    "franchise",
    "action",
    "operation",
    "planogram_template_name",
  ],

  CONFIGURED_TECHNICIAN_VENDING_MACHINE_TABLE: [
    "name",
    "state",
    "live_stock",
    "failure",
    "temperature",
    "board_firmware_version",
    "franchise",
    "action",
    "operation"
  ],

  DELETED_VENDING_MACHINES_TABLE: [
    "name",
    "deleted_by",
    "reason",
    "franchise",
    "date_created",
    "date_selected",
  ],
  HARDWARE_BOARD_TABLE: [
    "hardware_device",
    "carrier_board",
    "franchise",
    "action",
  ],

  AVAILABLE_VENDING_MACHINE_TABLE: [
    "signature",
    "franchise",
    "address",
    "board_type",
    "action",
  ],
  REFILL_MANAGEMENT_TABLE: [
    "refill_order",
    "refill_plan_id",
    "vm_name",
    "source_store",
    "state",
    "refill_partner",
    // "return_order_id",
    "executed_by",
    "remarks",
    "action",
    "last_updated",
  ],
  STORE_INVENTORY_TABLE: [
    "sku",
    "name",
    "category",
    "stock",
    "price",
    "updated_at",
  ],
  REFILL_VIEW_TABLE: [
    "sku",
    "name",
    "label",
    "price",
    "old_stock",
    "items_diff",
    "new_stock",
    "expiry_time",
  ],
  TOPUP_VIEW_TABLE: ["sku", "name", "label", "price", "diff_stock", "stock"],
  VM_VIEW_TABLE: [
    "sku",
    "name",
    "label",
    "price",
    "current_stock",
    "expiry_date",
  ],
  REFILL_SKU_TABLE: ["sku", "name", "price", "qty_added", "qty_removed"],
  TOPUP_SKU_TABLE: ["sku", "name", "price", "qty_added"],
  TALLY_SKU_TABLE: ["sku", "name", "price", "adjusted_qty"],
  TALLY_VIEW_TABLE: [
    "sku",
    "name",
    "expiry_time",
    "label",
    "price",
    "qty",
    "adjusted_qty",
  ],
  VM_SKU_TABLE: ["sku", "name", "price", "qty_added", "qty_removed"],
  DASHBOARD_FRANCHISE_TABLE: ["name", "sales"],
  DASHBOARD_RETAILER_TABLE: ["name", "sales"],
  DASHBOARD_VM_TABLE: ["name", "franchise", "sales"],
  DASHBOARD_BRAND_TABLE: ["name", "sales"],
  DASHBOARD_PRODUCT_TABLE: ["name", "category", "items_purchased"],
  PURCHASE_ORDER_TABLE: [
    "order_id",
    "order_date",
    "franchise",
    "vm",
    "order_source",
    "payment_status",
    "payment_mode",
    "amount",
    "vend_status",
    "refund_status",
  ],
  DIRECT_SALE_ORDER_TABLE: [
    "order_id",
    "order_date",
    "franchise",
    "vm",
    "order_source",
    "payment_status",
    "payment_mode",
    "amount",
    // "refund_status",
    "state",
  ],
  ORDER_CART_TABLE: [
    "sku",
    "name",
    "quantity",
    "coil",
    "price",
    "discount",
    "sub_offer_id",
    "items_vend",
    "vend_status",
    "failure_reason",
    "total_amount",
  ],
  PLANOGRAM_TEMPLATE_LIST: [
    "planogram_name",
    "linked_vm_template",
    "linked_vms",
    "entity",
    "entity_name",
    "last_updated",
    "action",
  ],
  VM_FEEDBACK_TABLE: ["rating", "title", "description", "created_at"],
  PRODUCT_FEEDBACK_TABLE: [
    "rating",
    "title",
    "description",
    "vm",
    "created_at",
  ],

  REFILL_LIST_VIEW_TABLE: [
    "slot",
    "sku",
    "name",
    "price",
    "current_Qty/Depth",
    "qty_to_be_removed",
    "qty_to_be_added",
    "expiry_time",
    "change_item",
  ],
  RETURN_LIST_VIEW_TABLE: [
    "return_order_id",
    "created_by",
    "state",
    "destination_store_warehouse",
    "last_updated",
    "refill_plan_id",
    "action",
  ],
  RETURN_ORDER_TABLE: [
    "sku",
    "product_name",
    "expiry_time",
    "slot",
    "price",
    "removed_quantity",
  ],
  REFUND_TABLE: [
    "name",
    "quantity",
    "coil",
    "refund_amount",
    "status",
    "created_at",
    "updated_at",
    "refund_reason",
    "utr_id",
  ],

  LOW_QTY_DATA: [
    "product_name",
    "warehouse_inventory",
    "inventory_already_in_vm",
    "inventory_tobefilled_in_vm",
  ],
  SALES_ANALYTICS_SINGULAR_TABLE: ["columnOne", "columnTwo"],
  SALES_ANALYTICS_GROUPED_TABLE: ["columnOne", "columnTwo", "columnThree"],
  COUPON_TABLE: [
    "code",
    "type",
    "start_date",
    "end_date",
    "duration",
    "updated_at",
    "action",
  ],
  DISCOUNT_TABLE: [
    "name",
    "type",
    "who_created",
    "entity",
    "entity_name",
    "status",
    "duration",
    "action",
    "vm",
    "#",
    "start_date",
    "end_date",
  ],
  DISCOUNT_PRODUCTS_FLAT: ["sku", "name", "price", "flatOff", "maxBuyQuantity"],
  CAMPAIGN_SUB_OFFER_TABLE: [
    "sku",
    "name",
    "offer_type",
    "actual_price",
    "off",
    "offer_price",
    "max_offer_qty",
    "total_stock",
    "action",
  ],
  DISCOUNT_PRODUCTS_PERCENTAGE: [
    "sku",
    "name",
    "price",
    "percentageOff",
    "maxBuyQuantity",
  ],
  DISCOUNT_PRODUCTS_BUYXFORY: [
    "sku",
    "name",
    "buyQuantity",
    "comboPrice",
    "offeredPrice",
  ],
  DISCOUNT_PRODUCTS_BUYXGETY: [
    "sku",
    "name",
    "buyQuantity",
    "comboPrice",
    "freeQuantity",
  ],
  SALES_TEMPLATE_LIST: ["name", "created_at", "action"],
  ADD_SUPPLIER_PRODUCT_SELECTION: [
    "sku",
    "name",
    "category",
    "status",
    "selling_price",
    "net_weight",
  ],
  SUPPLIER_LIST_TABLE: [
    "name",
    "email_id",
    "contact_person",
    "mobile_number",
    "last_updated",
    "action",
  ],
  ADD_SUPPLIER_TERMS_OF_SERVICE: [
    "sku",
    "name",
    "min_quantity",
    "price_calculation",
    "action",
  ],
  SUPPLIER_VIEW_PRODUCTS_TABLE: [
    "sku",
    "name",
    "selling_price",
    "net_weight",
    "min_quantity",
    "price_calculation",
  ],
  PURCHASE_ORDER_TABLE_LIST: [
    "invoice",
    "date_created",
    "supplier",
    "warehouse",
    "status",
    "cost",
    "user",
    "last_updated",
    "action",
  ],

  PURCHASE_RETURN_TABLE_LIST: [
    "return_id",
    "order_id",
    "date_created",
    "total_product_qty",
    "amount",
    "state",
    "action",
  ],

  PURCHASE_RETURN_VIEW_TABLE: [
    "sku",
    "name",
    "category",
    "qty",
    "price",
    "total",
    "reason",
  ],

  ADD_PURCHASE_ORDER_WAREHOUSE_SELECTION: [
    "name",
    "zone",
    "state",
    "city",
    "address",
    "action",
  ],
  MODIFY_PURCHASE_ORDER_PRODUCT_SELECTION: [
    "sku",
    "name",
    "category",
    "selling_price",
    "net_weight",
    "stock",
  ],
  MODIFY_PURCHASE_ORDER_PRODUCT_SUPPLIER_SELECTION: [
    "sku",
    "name",
    "stock",
    "supplier",
    "purchase_quantity",
    "price",
  ],
  MODIFY_PURCHASE_ORDER_PRODUCT_SUPPLIER_REVIEW: [
    "sku",
    "name",
    "stock",
    "supplier",
    "purchase_quantity",
    "price",
  ],
  PURCHASE_ORDER_VIEW_TABLE: [
    "sku",
    "name",
    "category",
    "old_stock",
    "price",
    "purchased_quantity",
    "received_quantity",
    "purchased_price",
    "total_amount",
  ],

  SALES_ORDER_VIEW_TABLE: [
    "sku",
    "name",
    "category",
    "price",
    "status",
    "quantity",
  ],

  PURCHASE_ORDER_MANAGE_VIEW_TABLE: [
    "sku",
    "name",
    "category",
    "old_stock",
    "price",
    "purchased_quantity",
    "purchased_price",
    "total_amount",
  ],
  PURCHASE_ORDER_MANAGE_VIEW_TABLE_WITH_INPUTS: [
    "sku",
    "name",
    "category",
    "selling_price",
    "net_weight",
    "stock",
    "supplier",
    "purchased_quantity",
    "purchased_price",
    "received_quantity",
    "paid_amount",
    "checkbox",
  ],
  STORE_MANAGER_LIST_TABLE: [
    "name",
    "mobile",
    "store",
    "is_active",
    "last_updated",
    "action",
  ],
  STORE_MANAGER_STORE_SELECTION: [
    "name",
    "vending_machines",
    "address",
    "store_button",
  ],
  VM_TEMPLATE_LIST_TABLE: [
    "name",
    "rows_cols",
    "planogram_cols",
    "entity",
    "entity_name",
    "last_updated",
    "action",
  ],
  VM_TEMPLATE_VIEW_PMS: ["name"],
  REFILL_PARTNER_TEMPLATE_VIEW_VMS: ["name"],
  BULK_REFILL_LIST_TABLE: [
    "name",
    "vm_template",
    "status",
    "last_updated",
    "action",
  ],
  BULK_REFILL_OVERVIEW_TABLE: [
    "vending_machine",
    "refill_plan",
    "status",
    "delivery_person",
    "action",
  ],
  VM_SETTINGS_LIST_TABLE: [
    "name",
    "vm_brand",
    "vm_carrier",
    "last_updated",
    "action",
  ],
  REFILL_PLAN_RETURN_ORDER: ["sku", "name", "removed_quantity"],
  TRANSACTION_EMAIL_REPORT_COLUMNS: ["column"],
  BULK_REFILL_POPUP_VM_LIST: ["store_warehouse", "vending_machine"],
  VM_SNAPSHOT_EXPIRY_DATES_MODAL: ["depth_no", "expiry_date"],

  ADD_MULTIPLE_VM_FOR_PLANOGRAM: ["vm", "warehouse", "store"],

  STORE_RETURN_ORDERS_WAREHOUSE_TABLE: [
    "store_return_id",
    "return_date",
    "source_store",
    "status",
    "action",
  ],
  VM_RETURN_ORDERS_WAREHOUSE_TABLE: [
    "vm_return_id",
    "created_by",
    "return_state",
    "source_vm",
    "last_updated",
    "refill_id",
    "action",
  ],
  LINKED_WAREHOUSE_MANAGERS_TABLE: ["name"],
  LINKED_STORE_MANAGERS_TABLE: ["name"],
  USER_OVERVIEW_TABLE: ["linked_1", "linked_2"],
  STORE_RETURN_ORDERS_OVERVIEW_WAREHOUSE_TABLE: [
    "sku",
    "product_name",
    "price",
    "return_quantity",
  ],
  SUPPLIER_RETURN_OVERVIEW_TABLE: [
    "sku",
    "name",
    "category",
    "price",
    "status",
    "old_stock",
    "stock_returned",
  ],
  MATERIAL_TRANSFER_OVERVIEW_TABLE: [
    "sku",
    "name",
    "category",
    "price",
    "status",
    "old_stock",
    "stock_transfer",
  ],

  MATERIAL_TRANSFER_OVERVIEW_QR_TABLE: [
    "sku",
    "name",
    "category",
    "transfer_qty",
    "stock",
    "price",
    "partial_qty",
    "checkbox",
  ],

  TRANSFER_ORDER_OVERVIEW_TABLE: [
    "sku",
    "name",
    "category",
    "price",
    "status",
    "stock",
  ],

  REQUEST_ORDER_OVERVIEW_TABLE: [
    "sku",
    "name",
    "category",
    "price",
    "status",
    "stock",
  ],

  KIT_TRANSFER_OVERVIEW_TABLE: [
    "sku",
    "name",
    "category",
    "price",
    "status",
    "quantity",
  ],
  TAllY_INCREASE_TABLE: [
    "sku",
    "name",
    "category",
    "price",
    "last_updated",
    "old_stock",
    "new_stock",
    "adjusted",
  ],
  TAllY_DECREASE_TABLE: [
    "sku",
    "name",
    "category",
    "price",
    "last_updated",
    "old_stock",
    "new_stock",
    "adjusted",
  ],
  TAllY_FAILED_TABLE: [
    "sku",
    "name",
    "category",
    "price",
    "last_updated",
    "actual_qty",
    "adjusted",
  ],
  TAllY_NO_CHANGE_TABLE: [
    "sku",
    "name",
    "category",
    "price",
    "last_updated",
    "old_stock",
    "new_stock",
    "adjusted",
  ],

  STORE_RETURN_ORDERS_TABLE: [
    "store_return_id",
    "return_date",
    "destination_warehouse",
    "status",
    "action",
  ],

  VM_RETURN_ORDERS_OVERVIEW_TABLE: [
    "sku",
    "product_name",
    "price",
    "return_quantity",
  ],
  STORE_STORE_RETURN_ORDERS_OVERVIEW_TABLE: [
    "sku",
    "product_name",
    "return_quantity",
  ],
  STORE_VM_RETURN_ORDERS_TABLE: [
    "vm_return_id",
    "created_by",
    "return_state",
    "source_vm",
    "last_updated",
    "refill_id",
    "action",
  ],
  RECEIVE_RETURN_ORDER_TABLE: ["sku", "product_name", "return_quantity"],
  CREATE_STORE_RETURN_ORDER: [
    "sku",
    "product_name",
    "current_quantity",
    "return_quantity",
  ],

  CREATE_FINANCE_REPORT_LIST: [
    "report_Id",
    "from_Date",
    "to_Date",
    "status",
    "url",
  ],

  CREATE_RETAILERS_LIST: [
    "name",
    "franchise",
    "total_boards",
    "states",
    "cities",
    "last_updated",
    "action",
  ],
  CREATE_REPORT_LIST: [
    // "request_id",
    "name",
    "created_at",
    "scope_type",
    "report_source",
    "download",
  ],
  CREATE_PURCHASE_ORDER_TABLE: [
    "name",
    "price",
    "available_qty",
    "purchase_qty",
    "purchase_price",
    "total",
    "action",
  ],
  CREATE_PURCHASE_RETURN_TABLE: [
    "sku",
    "name",
    "purchase_qty",
    "purchase_price",
    "return_qty",
    "reason",
    "checkbox",
  ],
  CREATE_SHIPPING_ORDER_TABLE: [
    "name",
    "price",
    "available_qty",
    "shipping_qty",
    "action",
  ],
  CREATE_TALLY_ORDER_INVENTORY_TABLE: [
    "name",
    "price",
    "available_qty",
    "new_qty",
    "adjusted",
    "action",
  ],
  WAREHOUSE_STOCK_TABLE: [
    "sku",
    "name",
    "brand",
    "category",
    "price",
    "status",
    "stock",
    "last_updated",
  ],

  CUSTOMER_FEEDBACK_LIST: [
    "order_id",
    "vending_machine",
    "rating",
    "comment",
    "order_status",
    "created_at",
  ],

  ADVERTISEMENT_LIST: [
    "name",
    "video_type",
    "status",
    "linked_vms",
    "updated_at",
    "action",
  ],
  VM_FAILURE: ["name", "count"],
  VM_REVENUE: ["name", "sales" , "count"],
  VM_OFFLINE: ["name", "since", "duration"],
  VM_MAINTENANCE_LIST: ["name", "since", "duration", "reason"],
  TOP_SELLING_PRODUCTS: ["name", "sales", "units"],
  LINK_REFILL_PARTNER_TABLE: [
    "refiller_id",
    "name",
    "entity",
    "entity_name",
    "view_access",
    "request_order",
    "transfer_order",
  ],
  EDIT_LINK_REFILL_PARTNER_TABLE: [
    "refiller_id",
    "name",
    "entity",
    "entity_name",
    "action",
  ],
  LINK_WAREHOUSE_MANAGER_TABLE: [
    "name",
    "entity",
    "entity_name",
    "wh",
    "view_access",
    "request_order",
    "modify_access",
    "transfer_order",
    "purchase_order",
  ],
  LINK_WAREHOUSE_WM_TABLE: [
    "name",
    "address",
    "entity",
    "view_access",
    "request_order",
    "modify_access",
    "transfer_order",
    "purchase_order",
  ],
  EDIT_LINK_WAREHOUSE_MANAGER_TABLE: [
    "name",
    "entity",
    "entity_name",
    "wh",
    "action",
  ],
  EDIT_LINK_WAREHOUSE_FOR_WM_TABLE: [
    "name",
    "address",
    "entity",
    "entity_name",
    "wh",
    "action",
  ],
  CUSTOMER_PLATFORM_LIST: ["name", "code"],
  PLATFORM_LINKED_VM_LIST: ["name", "uuid", "restricted_products", "unlink"],
  VM_RESTRICTED_PRODUCT_LIST: ["sku", "name", "price"],
  LINK_VENDING_MACHINE_TABLE: [
    "name",
    "address",
    "retailer",
    "zone",
    "selected",
  ],
  TECHNICIAN_LINK_VENDING_MACHINE_TABLE: [
    "name",
    "address",
    "retailer",
    "zone",
    "selected",
  ],
  LINK_WAREHOUSE_TABLE: [
    "name",
    "address",
    "entity",
    "view_access",
    "request_order",
    "transfer_order",
  ],

  LINK_WAREHOUSE_TECHNICIAN_TABLE: [
    "name",
    "address",
    "entity",
    "view_access",
    // "request_order",
    "transfer_order",
  ],
  EDIT_LINK_WAREHOUSE_TABLE: [
    "name",
    "address",
    "entity",
    "entity_name",
    "action",
  ],
  LINK_VEHICLE_TABLE: [
    "name",
    "vehicle",
    "entity",
    "view_access",
    "request_order",
    "transfer_order",
  ],
  EDIT_LINK_VEHICLE_TABLE: ["name", "vehicle", "entity", "action"],
  NEGATIVE_ITEM_LIST: [
    "name",
    "start_date",
    "end_date",
    "vm",
    "products",
    "status",
    "source",
    "action",
  ],
  LINK_ITEM_TABLE: ["sku", "name", "brand", "category", "selected"],
  REQUEST_PRODUCT_VIEW: ["sku", "name", "urgency", "preferable_time", "status"],
  KIT_VM_TABLE: ["name", "address", "retailer", "zone", "selected"],
  KIT_PREVIEW_MACHINE_TABLE: [
    "name",
    "address",
    "warehouse",
    "select_vm_extra_item",
  ],
  KIT_STOCK_AVAILABILITY_TABLE: [
    "sku",
    "name",
    "qty",
    "weight",
    "price",
    "action",
  ],
  KIT_ADD_MORE_ITEM_TABLE: ["sku", "name", "price", "weight", "qty"],
  SALE_ORDER_VIEW_TABLE: ["sku", "name", "category", "price", "status", "qty"],
  CASH_IN_HAND_ORDER_TABLE: [
    "order_id",
    "vm",
    "expected_amount",
    "pickedup_amount",
  ],
  DIRECT_SALE_TABLE: ["id", "amount", "refund_amount", "date"],
  OFFER_VIEW_TABLE: [
    "sku",
    "name",
    "offer_type",
    "actual_price",
    "off",
    "offer_price",
    "max_Offer_qty",
    "total_stock",
    "status",
  ],
  SUB_OFFER_VIEW_TABLE: [
    "sku",
    "name",
    "offer_type",
    "actual_price",
    "off",
    "offer_price",
    "max_Offer_qty",
    "total_stock",
  ],
  PG_VIEW_TABLE: [
    "franchise",
    "retailer",
    "pg_default",
    "pg_device",
    "pg_mobile",
    "pg_web",
    "pg_card",
    "pg_link",
    "action",
  ],
  ACTION_HISTORY_TABLE: [
    "order_type",
    "timestamp",
    "order_id",
    "vm",
    "state",
    "user_id",
  ],

  MOTOR_DETAILS: ["coil", "container", "row", "column"],
  MACHINE_EVENT_LOG: [
    "created_at",
    "vm_name",
    "current_state",
    "event_name",
    "event_code",
    "level",
    "resulting_state",
    "reason",
    "origin",
  ],
};

// Shipping status Keys is used for matching Shipping status values
export const SHIPPING_STATUS_KEYS = {
  ORDER_CREATED: "ORDER_CREATED",
  IN_PROGRESS: "IN_PROGRESS",
  DISPATCHED: "DISPATCHED",
  DELIVERED: "DELIVERED",
  CANCELLED: "CANCELLED",
  DELIVERED_DP: "DELIVERED_DP",
  RECEIVED_SM: "RECEIVED_SM",
  IN_TRANSIT: "IN_TRANSIT",
};

export const SHIPPING_ORDER_STATUS = {
  [SHIPPING_STATUS_KEYS.ORDER_CREATED]: {
    displayName: "Order Created",
    color: "#3987FD",
    shippingIndex: 1,
  },
  [SHIPPING_STATUS_KEYS.IN_PROGRESS]: {
    displayName: "In Progress",
    color: "#FBC658",
    shippingIndex: 2,
  },
  [SHIPPING_STATUS_KEYS.DISPATCHED]: {
    displayName: "Dispatched",
    color: "#4B6E86",
    shippingIndex: 3,
  },
  [SHIPPING_STATUS_KEYS.DELIVERED]: {
    displayName: "Received",
    color: "#2C8300",
    shippingIndex: 5,
    forNotKey: SHIPPING_STATUS_KEYS.RECEIVED_SM,
  },
  [SHIPPING_STATUS_KEYS.CANCELLED]: {
    displayName: "Cancelled",
    color: "#FF2E43",
    shippingIndex: null,
  },
  [SHIPPING_STATUS_KEYS.DELIVERED_DP]: {
    displayName: "Delivered",
    color: "#f95a3f",
    shippingIndex: 4,
    forNotKey: SHIPPING_STATUS_KEYS.IN_TRANSIT,
  },
  [SHIPPING_STATUS_KEYS.IN_TRANSIT]: {
    displayName: "In Transit",
    color: "#f95a3f",
    shippingIndex: 4,
    forKeyOnly: SHIPPING_STATUS_KEYS.IN_TRANSIT,
  },
  [SHIPPING_STATUS_KEYS.RECEIVED_SM]: {
    displayName: "Received",
    color: "#2C8300",
    shippingIndex: 5,
    forKeyOnly: SHIPPING_STATUS_KEYS.RECEIVED_SM,
  },
};

export const REFILL_MANAGEMENT_STATUS = {
  CREATED: {
    displayName: "Created",
    color: "#3987FD",
  },
  IN_TRANSIT: {
    displayName: "In Transit",
    color: "#FBC658",
  },
  EXECUTED: {
    displayName: "Executed",
    color: "#2C8300",
  },
};
export const MATERIAL_TRANSFER_STATUS = {
  CREATED: {
    displayName: "Created",
    color: "#3987FD",
  },
  INTRANSIT: {
    displayName: "In Transit",
    color: "#FBC658",
  },
  EXECUTED: {
    displayName: "Executed",
    color: "#2C8300",
  },
  PARTIALLY_RECEIVED: {
    displayName: "Partially Received",
    color: "#FFC41E",
  },
  SHIPPED: {
    key: "SHIPPED",
    displayName: "Shipped",
    color: "#e139fd",
  },
  RECEIVED: {
    key: "RECEIVED",
    displayName: "Received",
    color: "#4CB04E",
  },
  CANCELLED: {
    key: "CANCELLED",
    displayName: "Cancelled",
    color: "#FF2E43",
  },
  DRAFT: {
    key: "DRAFT",
    displayName: "Draft",
    color: "#e139fd",
  },
};
export const ITEMS_LIMIT = [10, 20, 25];
export const NEW_ITEMS_LIMIT = [20, 50, 100];
export const ASC = "asc";
export const DESC = "desc";

export const NOTIFICATION_TIME_PERIOD_ARR = [
  {
    name: "All",
    value: "all",
  },
  {
    name: "Today",
    value: "day",
  },
  {
    name: "Week",
    value: "week",
  },
  {
    name: "Month",
    value: "month",
  },
  {
    name: "Year",
    value: "year",
  },
];
// real time notification types to check notification
export const NOTIFICATION_TYPES_KEY = {
  REFILL_EXECUTE: "REFILL_EXECUTE",
  VM_STATUS: "VM_STATUS",
};
export const NOTIFICATION_TYPES = {
  REFILL_EXECUTE: "Refill Executed",
  ABOUT_TO_RUNOUT: "About to Runout",
  OUT_OF_STOCK: "Out of Stock",
  PRODUCT_REQUEST: "Product Requested",
  PRODUCT_EXPIRED: "Product Expired",
  VEND_FAILURE: "Vend Failure",
  PARTIAL_VEND_FAILURE: "Partial Vend Failure",
  PAYMENT_TIMEDOUT: "Payment Timedout",
  VM_STATUS: "Vending Machine Status",
  MANUAL_REFUND: "Manual Refund",
  AUTO_REFUND: "Auto Refund",
  CANCELLED_BY_CONSUMER: "Cancelled by Consumer",
  VEND_SUCCESS: "Vend Success",
  DISCOUNT_EXPIRED: "Discount Expired",
  TRANSACTION_REPORT_RESPONSE: "Transaction Reports",
  REFILL_PLAN_CREATED_BY_REFILL_PARTNER: "Refill Plan/Return Order",
  REFILL_PLAN_DELETED_BY_REFILL_PARTNER: "Refill Plan/Return Order",
  LOW_INVENTORY_WAREHOUSE: "Low Warehouse Inventory",
  HARDWARE_BOARD_ONBOARD: "Hardware Board Onboard",
  VM_MOTOR: "Motors Status",
};

export const SALES_ANALYTICS_PRODUCTS_ARR = {
  product: "Product",
  product_category: "Product Category",
  product_brand: "Product Brand",
  product_unit: "Product Unit",
};

export const SALES_ANALYTICS_LOCATIONS_ARR = {
  franchise: "Franchise",
  state: "State",
  city: "City",
  zone: "Zone",
  vending_machine: "Vending Machine",
};

export const SALES_ANALYTICS_RANGE_ARR = [
  {
    value: "top_10",
    name: "Top 10",
  },
  {
    value: "top_20_percent",
    name: "Top 20%",
  },
  {
    value: "bottom_10",
    name: "Bottom 10",
  },
  {
    value: "bottom_20_percent",
    name: "Bottom 20%",
  },
  {
    value: "all",
    name: "All",
  },
  {
    value: "manual",
    name: "Select Manually",
  },
];

export const TIME_FILTER_OPTIONS = [
  { id: 1, value: "today", name: "Today" },
  { id: 2, value: "yesterday", name: "Yesterday" },
  {
    id: 3,
    value: "custom",
    name: "Custom Range",
  },
];

//Enumerations
export const ENUMS = {
  COUPON_TYPE: { FLAT: "FLAT", PERCENTAGE: "PERCENTAGE" },
  ALL_DROPDOWN_OPTION: { ID: "ALL", TEXT: "All" },
  DISCOUNT_TYPE: {
    FLAT: "FLAT",
    PERCENTAGE: "PERCENTAGE",
    BUY_X_FOR_Y: "COMBO_TYPE_II",
    BUY_X_GET_Y: "COMBO_TYPE_I",
  },
  COMPANY_TYPE: {
    WITH_FRANCHISE: "WITH_FRANCHISE",
    WITHOUT_FRANCHISE: "WITHOUT_FRANCHISE",
  },
  FRANCHISE_TYPE: {
    DEPENDENT: {
      type: "DEPENDENT",
      title: "Dependent Franchise",
      description: "This franchise won’t have any stores",
    },
    INDEPENDENT: {
      type: "INDEPENDENT",
      title: "Independent Franchise",
      description: "This franchise will have stores",
    },
  },
};

//Status codes for api.
export const API_STATUS_CODES = {
  LICENSE_EXPIRED: 402,
};

// Variables default values.
export const DEFAULT_VALUES = {
  STAR_RATING: 5,
  DOUBLE_DASH: "--",
  ZERO: "0",
  NOT_APPLICABLE: "N/A",
  PERCENTAGE: "%",
  DEPENDENT_FRANCHISE_STORE_MSG: "No store required for this franchise",
};

//User roles
export const USER_ROLES = {
  COMPANY_ADMIN: "ROLE_COMPANY_ADMIN",
  COMPANY_PARTNER: "ROLE_COMPANY_PARTNER",
  FRANCHISE_ADMIN: "ROLE_FRANCHISE_ADMIN",
  FRANCHISE_LOCATION_PARTNER: "ROLE_FRANCHISE_LOCATION_PARTNER",
  STORE_MANAGER: "ROLE_STORE_MANAGER",
  WAREHOUSE_MANAGER: "ROLE_WAREHOUSE_MANAGER",
  OPERATOR: "ROLE_OPERATOR",
  RETAILER_ADMIN: "ROLE_RETAILER_ADMIN",
  ROLE_TECHNICIAN_MANAGER: "ROLE_TECHNICIAN_MANAGER",
  CUSTOMER_PLATFORM_ADMIN: "ROLE_CUSTOMER_PLATFORM_ADMIN" ,
};

//User roles
export const USER_ROLES_ID = {
  COMPANY_ADMIN_ID: 1,
  COMPANY_PARTNER_ID: 2,
  FRANCHISE_ADMIN_ID: 3,
  FRANCHISE_LOCATION_PARTNER_ID: 9,
  STORE_MANAGER_ID: 8,
  WAREHOUSE_MANAGER_ID: 7,
  OPERATOR_ID: 6,
  RETAILER_ADMIN_ID: 5,
  TECHNICIAN_MANAGER_ID: 10,
  CUSTOMER_PLATFORM_ADMIN_ID: 11 , 
};

//Vending machine boards name
export const VENDIFY_BOARD_NAMES = {
  PINE64: "Vendi-Pro Board (with Display)",
  ESP32: "Vendi-Mini Board (without Display)",
};

//prevent any function default behaviour.
export const preventDefaultBehaviour = (e) => {
  e.preventDefault();
};

//Manual error messages for the app.
export const ERROR_MESSAGES = {
  COUPON_MAX_OFF: `Maximum off should be less than minimum cart value & atleast 1.`,
  COUPON_MIN_CART: "Minimum cart value should be at least 1.",
  COUPON_FLAT_OFF:
    "Flat off should be atleast 1 and less than minimum cart value.",
  COUPON_PERCENT_OFF: "Percentage off should be atleast 1 and less than 100.",
  COUPON_MAX_REDEMPTION: "Maximum redemption should be atleast 1.",
  COUPON_DISCOUNT_LESS_THAN_ONE: "Minimum discount price should be 1.",
  DISCOUNT_FLAT_OFF: "Flat off should be less than price.",
  DISCOUNT_MAX_BUY_QUANTITY: "Max buy quantity should be atleast 1.",
  DISCOUNT_PERCENT_OFF: "Percentage off should be atleast 1 and less than 100.",
  DISCOUNT_BUY_QUANTITY: "Buy quantity should be atleast 1.",
  DISCOUNT_OFFERED_PRICE: "Offered price should be less than combo price.",
  DISCOUNT_FREE_QUANTITY: "Free quantity should be less than buy quantity.",
};

//Keyboard codes
export const KEY_CODES = {
  SPACE_KEY: "Space",
  DOT_KEY: "Period",
  ENTER_KEY: "Enter",
  NUMPAD_ENTER_KEY: "NumpadEnter",
  NEGATIVE_KEY: "Minus",
  NUMPAD_DOT_KEY: "NumpadDecimal",
};

export const REG_EXPS = {
  ALPHABETS_AND_NUMBERS: /^[a-zA-Z0-9]*$/,
  ALPHABETS_ONLY: /^[a-zA-Z]*$/,
  ALPHABETS_AND_SPACE: /^[a-zA-Z][a-zA-Z ]*$/,
  NUMBERS_ONLY: /^[0-9]*$/,
  ALPHABETS_NUMBERS_AND_SPECIAL_CHARACTERS:
    /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[!@#$%^&*()_-]).{8,}$/,
};

export const REFILL_ORDER_TYPES = {
  REFILL: "Refill",
  RETURN: "Return",
  TALLY: "Tally",
  CASH: "Cash",
  TOP_UP: "TopUp",
};

//Discount types rule for validation
export const DISCOUNT_TYPE_PRODUCTS_RULE = {
  [ENUMS.DISCOUNT_TYPE.FLAT]: {
    isArrayJson: true,
    get_value: {
      required: true,
      minVal: 1,
      number: true,
      errorMessage: ERROR_MESSAGES.DISCOUNT_FLAT_OFF,
    },
    max_buy_quantity: {
      minVal: 1,
      number: true,
      errorMessage: ERROR_MESSAGES.DISCOUNT_MAX_BUY_QUANTITY,
    },
    separator: "id",
    hasDynamicRules: true,
    dynamicRules: {},
    required: true,
  },
  [ENUMS.DISCOUNT_TYPE.PERCENTAGE]: {
    isArrayJson: true,
    get_value: {
      required: true,
      minVal: 1,
      maxVal: 99,
      number: true,
      errorMessage: ERROR_MESSAGES.DISCOUNT_PERCENT_OFF,
    },
    max_buy_quantity: {
      minVal: 1,
      number: true,
      errorMessage: ERROR_MESSAGES.DISCOUNT_MAX_BUY_QUANTITY,
    },
    separator: "id",
    required: true,
  },
  [ENUMS.DISCOUNT_TYPE.BUY_X_FOR_Y]: {
    isArrayJson: true,
    buy_quantity: {
      required: true,
      minVal: 1,
      number: true,
      errorMessage: ERROR_MESSAGES.DISCOUNT_BUY_QUANTITY,
    },
    get_value: {
      required: true,
      minVal: 1,
      number: true,
      errorMessage: ERROR_MESSAGES.DISCOUNT_OFFERED_PRICE,
    },
    separator: "id",
    hasDynamicRules: true,
    dynamicRules: {},
    required: true,
  },
  [ENUMS.DISCOUNT_TYPE.BUY_X_GET_Y]: {
    isArrayJson: true,
    buy_quantity: {
      required: true,
      minVal: 1,
      number: true,
      errorMessage: ERROR_MESSAGES.DISCOUNT_BUY_QUANTITY,
    },
    get_value: {
      required: true,
      minVal: 1,
      number: true,
      errorMessage: ERROR_MESSAGES.DISCOUNT_FREE_QUANTITY,
    },
    separator: "id",
    hasDynamicRules: true,
    dynamicRules: {},
    required: true,
  },
};

//Whenever we want a function to do nothing, this helps.
export const DUMMY_FUNCTION = () => {};

export const SYMBOLS = {
  PERCENTAGE: "%",
};

//Statuses for purchase orders.
export const PURCHASE_ORDER_STATUSES = {
  CREATED: { key: "CREATED", displayText: "Created", displayColor: "#3987FD" },
  PARTIALLY_RECEIVED: {
    key: "PARTIALLY_RECEIVED",
    displayText: "Partially Received",
    displayColor: "#FFC41E",
  },
  RECEIVED: {
    key: "RECEIVED",
    displayText: "Received",
    displayColor: "#4CB04E",
  },
  CANCELLED: {
    key: "CANCELLED",
    displayText: "Cancelled",
    displayColor: "#FF2E43",
  },

  SHIPPED: {
    key: "SHIPPED",
    displayText: "Shipped",
    displayColor: "#e139fd",
  },
  EXECUTED: {
    key: "EXECUTED",
    displayText: "Executed",
    displayColor: "#e139fd",
  },
  DRAFT: {
    key: "DRAFT",
    displayText: "Draft",
    displayColor: "#e139fd",
  },
};

//List of popular browsers with how to detect them using regular expression.
export const BROWSER_TYPES = {
  CHROME: { name: "CHROME", detectRegExp: /chrome|chromium|crios/i },
  FIREFOX: { name: "FIREFOX", detectRegExp: /firefox|fxios/i },
  SAFARI: { name: "SAFARI", detectRegExp: /safari/i },
  OPERA: { name: "OPERA", detectRegExp: /opr\//i },
  EDGE: { name: "EDGE", detectRegExp: /edg/i },
};

//Returns user browser name.
export function detectBrowserType() {
  let userAgent = navigator.userAgent;

  return Object.values(BROWSER_TYPES).filter((browser) =>
    userAgent.match(browser.detectRegExp),
  )[0];
}

export const PAYMENT_ORIGINS = {
  1: { displayName: "Mobile Application" },
  2: { displayName: "Mobile Browser" },
  3: { displayName: "Device App" },
};

export const BULK_REFILL_STATUSES = {
  1: { displayName: "Unexecuted", colorCode: "#ff0018" },
  2: { displayName: "Partially Executed", colorCode: "#f9c83f" },
  3: { displayName: "Fully Executed", colorCode: "#4cb04e" },
  6: { displayName: "Not generated", colorCode: "#ff0018" },
};

export const REFILL_STATUSES_IN_BULK_REFILL = {
  1: { displayName: "Pending", colorCode: "#f9c83f" },
  2: { displayName: "Pending", colorCode: "#f9c83f" },
  3: { displayName: "Executed", colorCode: "#4cb04e" },
  4: { displayName: "Dispatched", colorCode: "#f9c83f" },
  6: { displayName: "Not generated", colorCode: "#ff0018" },
};

export const TRANSACTIONS_DATE_FILTER = {
  TODAY: "today",
  YESTERDAY: "yesterday",
  WEEK: "week",
  MONTH: "month",
  YEAR: "year",
  CUSTOM: "custom",
  ALL: "all",
};

export const LOCATION_COORDS = {
  DELHI: { latitude: 28.6448, longitude: 77.216721 },
};

export const DROP_SENSOR_STATUSES = {
  ACTIVE: { key: 1, display: "Active" },
  INACTIVE: { key: 2, display: "Inactive" },
  FAILED: { key: 3, display: "Failed" },
};

export const BOARD_EXPIRY_TIME_STATUS = [
  { id: 1, name: "Never show expiry to user" },
  { id: 2, name: "EXP DD/MM/YY HH:MM XM" },
  { id: 3, name: "Best before DD/MM/YY HH:MM XM" },
];

export const BOARD_STOCK_DISPLAY_FLAG = [
  { id: 0, name: "Don't show non available products in list view" },
  { id: 1, name: "Show non available products in the end" },
  { id: 2, name: "Show non available products as it is" },
];

export const VIDEO_TYPE = [
  { id: 1, name: "Idle Screen" },
  { id: 2, name: "Dispense Screen" },
  { id: 3, name: "External Screen" },
  { id: 4, name: "Home Screen" },
];

export const SCREEN_SIZE_OPTIONS = [20, 30, 40];

// Function to get the name based on advertisement_type
export function getVideoTypeName(advertisementType) {
  const type = VIDEO_TYPE.find((type) => type.id === advertisementType);
  return type ? type.name : "Unknown Type";
}

export const ADVERTISEMENT_S3_BUCKET_DIRECTRY = "vending-machine-advertisement";

export const WAREHOUSE_TYPE = [
  { id: 1, name: "Warehouse" },
  { id: 2, name: "Machine's Storage" },
  { id: 3, name: "Vehicle" },
  { id: 6, name: "Technician Warehouse" },
];

export const CHANNEL_TYPE = [
  { id: 1, name: "Internal" },
  { id: 2, name: "Retailfy" },
];

export const ENTITY_TYPE = [
  { id: 1, name: "Company" },
  { id: 2, name: "Zone" },
  { id: 3, name: "Retailer" },
];

export const ORIGIN_TYPE = [
  { id: 1, name: "ERP" },
  { id: 2, name: "Dhandha" },
  { id: 3, name: "Other/3rd Party" },
];

export const CAMPAIGN_OFFER_STATUS = [
  { id: 1, name: "Active" },
  { id: 2, name: "Paused" },
  { id: 3, name: "Suspended" },
  { id: 4, name: "Expired" },
];

export const OFFER_STATUS_DICT = {
  Active: 1,
  Paused: 2,
  Suspended: 3,
  Expired: 4,
};

export const OFFER_STATUS_NUMBERS = {
  1: "Resume",
  2: "Pause",
  3: "Suspend",
  4: "Expire",
};

export const REPEAT_TYPE = [
  { id: 1, name: "Daily" },
  { id: 2, name: "Weekdays (Mon-Fri)" },
  { id: 3, name: "Weekend (Sat & Sun)" },
  { id: 4, name: "Custom" },
];

export const OFFER_TYPES = {
  1: "Flat",
  2: "Percentage",
  3: "Combo",
  4: "Liquidation",
  5: "Buy X items get Y items",
  6: "Buy X product get Y product free",
  7: "Buy X items for Y total amount",
  8: "Shop X amount get Y item free",
};

export const REPEAT_DAYS_LIST = [
  {
    id: 1,
    name: "SUNDAY",
  },
  {
    id: 2,
    name: "MONDAYS",
  },
  {
    id: 3,
    name: "TUESDAY",
  },
  {
    id: 4,
    name: "WEDNESDAY",
  },
  {
    id: 5,
    name: "THURSDAY",
  },
  {
    id: 6,
    name: "FRIDAY",
  },
  {
    id: 7,
    name: "SATURDAY",
  },
];

export const REPEAT_TIME_SLOT = [
  { id: 1, name: "Full day - default" },
  { id: 2, name: "Late night (11pm-3am)" },
  { id: 3, name: "Early morning (3am-8am)" },
  { id: 4, name: "Custom" },
];

export const ENTITY_TYPE_DICT = { 1: "Company", 2: "Zone", 3: "Retailer" };

export const CHANNEL_OPTIONS_DICT = {
  1: " Customer care",
  2: "Feedback Forum",
  3: "Refill Partner App",
  4: "Consumer App",
  5: "Other",
};

export const REQUESTED_BY_OPTIONS_DICT = {
  1: "Admin",

  2: "Employee",

  3: "Management",

  4: "Student",

  5: "Other",
};

export const URGENCY_OPTIONS_DICT = {
  1: "Urgent",

  2: "Nice To have",

  3: "Take time",
};

export const PREFERABLE_TIME_OPTIONS_DICT = {
  1: "Early Morning",

  2: "Morning",

  3: "Afternoon",

  4: "Evening",

  5: "Night",

  6: "Late Night",

  7: "Always",
};

export const RestrictedRefillPermissions = [
  {
    id: 1,
    perm: "Refill Products",
    group: "Refill",
  },
  {
    id: 7,
    perm: "Set expiry of a product in the refill plan",
    group: "Refill",
  },
  {
    id: 13,
    perm: "Request Product",
    group: "Other",
  },
  {
    id: 17,
    perm: "Change PIN",
    group: "Other",
  },
];

export const EssentialRefillPermissions = [
  {
    id: 1,
    perm: "Refill Products",
    group: "Refill",
  },
  {
    id: 2,
    perm: "Add/ Delete products in the refill plan",
    group: "Refill",
  },
  {
    id: 3,
    perm: "Swap products in the refill plan",
    group: "Refill",
  },
  {
    id: 7,
    perm: "Set expiry of a product in the refill plan",
    group: "Refill",
  },
  {
    id: 9,
    perm: "Maintenance mode on/off setting",
    group: "Machine",
  },
  {
    id: 10,
    perm: "Refill mode on/off setting",
    group: "Machine",
  },
  {
    id: 13,
    perm: "Request Product",
    group: "Other",
  },
  {
    id: 17,
    perm: "Change PIN",
    group: "Other",
  },
];

export const StandardRefillPermissions = [
  {
    id: 1,
    perm: "Refill Products",
    group: "Refill",
  },
  {
    id: 2,
    perm: "Add/ Delete products in the refill plan",
    group: "Refill",
  },
  {
    id: 3,
    perm: "Swap products in the refill plan",
    group: "Refill",
  },
  {
    id: 4,
    perm: "Block/ unblock motors in the refill plan",
    group: "Refill",
  },
  {
    id: 6,
    perm: "Change depth of machine in the refill plan",
    group: "Refill",
  },
  {
    id: 7,
    perm: "Set expiry of a product in the refill plan",
    group: "Refill",
  },
  {
    id: 8,
    perm: "Get secret code for a machine to do free vend & change settings",
    group: "Machine",
  },
  {
    id: 9,
    perm: "Maintenance mode on/off setting",
    group: "Machine",
  },
  {
    id: 10,
    perm: "Refill mode on/off setting",
    group: "Machine",
  },
  {
    id: 11,
    perm: "Restart machine setting",
    group: "Machine",
  },
  {
    id: 12,
    perm: "See sales data of machine",
    group: "Machine",
  },
  {
    id: 13,
    perm: "Request Product",
    group: "Other",
  },
  {
    id: 15,
    perm: "Request Order",
    group: "Other",
  },
  {
    id: 17,
    perm: "Change PIN",
    group: "Other",
  },
];

export const DefaultTechnicianPermission = [
  {
    id: 1,
    perm: "Allow technician to block or unblock vending machine motor types.",
    code: "OP_BLOCK_MOTORS",
    group: "Operational",
  },
  {
    id: 2,
    perm: "Allow technician to perform motor actions (rotate motor, rotate all, mark faulty, etc.) of the vending machine.",
    code: "OP_MOTOR_ACTIONS",
    group: "Operational",
  },
  {
    id: 3,
    perm: "Allow technician to view the vending machine’s secret code.",
    code: "OP_VIEW_SECRET_CODE",
    group: "Operational",
  },
  {
    id: 4,
    perm: "Allow technician to access the vending machine’s MAC address.",
    code: "OP_ACCESS_MAC",
    group: "Operational",
  },
  {
    id: 5,
    perm: "Allow technician to read the current temperature of the vending machine.",
    code: "OP_READ_TEMP",
    group: "Operational",
  },
  {
    id: 6,
    perm: "Allow technician to set or adjust the vending machine temperature.",
    code: "OP_SET_TEMP",
    group: "Operational",
  },
  {
    id: 7,
    perm: "Allow technician to report an incident for the vending machine.",
    code: "OP_REPORT_INCIDENT",
    group: "Operational",
  },
  {
    id: 8,
    perm: "Allow technician to create job work.",
    code: "OP_CREATE_JOB",
    group: "Operational",
  },
  {
    id: 9,
    perm: "Allow technician to turn vending machine lights on or off.",
    code: "OP_MACHINE_LIGHTS",
    group: "Operational",
  },
  {
    id: 10,
    perm: "Allow technician to rotate the vending machine screen.",
    code: "OP_ROTATE_SCREEN",
    group: "Operational",
  },
  {
    id: 11,
    perm: "Allow technician to restart the vending machine.",
    code: "OP_RESTART_MACHINE",
    group: "Operational",
  },
  {
    id: 12,
    perm: "Allow technicians to open the vending machine location on map.",
    code: "OP_OPEN_MAP",
    group: "Operational",
  },
  {
    id: 26,
    perm: "Allow technician to configure location of the Vending Machine.",
    code: "OP_LOCATION_CONFIG",
    group: "Operational",
  },
];

export const FullTechnicianPermission = [
  {
    id: 1,
    perm: "Allow technician to block or unblock vending machine motor types.",
    code: "OP_BLOCK_MOTORS",
    group: "Operational",
  },
  {
    id: 2,
    perm: "Allow technician to perform motor actions (rotate motor, rotate all, mark faulty, etc.) of the vending machine.",
    code: "OP_MOTOR_ACTIONS",
    group: "Operational",
  },
  {
    id: 3,
    perm: "Allow technician to view the vending machine’s secret code.",
    code: "OP_VIEW_SECRET_CODE",
    group: "Operational",
  },
  {
    id: 4,
    perm: "Allow technician to access the vending machine’s MAC address.",
    code: "OP_ACCESS_MAC",
    group: "Operational",
  },
  {
    id: 5,
    perm: "Allow technician to read the current temperature of the vending machine.",
    code: "OP_READ_TEMP",
    group: "Operational",
  },
  {
    id: 6,
    perm: "Allow technician to set or adjust the vending machine temperature.",
    code: "OP_SET_TEMP",
    group: "Operational",
  },
  {
    id: 7,
    perm: "Allow technician to report an incident for the vending machine.",
    code: "OP_REPORT_INCIDENT",
    group: "Operational",
  },
  {
    id: 8,
    perm: "Allow technician to create job work.",
    code: "OP_CREATE_JOB",
    group: "Operational",
  },
  {
    id: 9,
    perm: "Allow technician to turn vending machine lights on or off.",
    code: "OP_MACHINE_LIGHTS",
    group: "Operational",
  },
  {
    id: 10,
    perm: "Allow technician to rotate the vending machine screen.",
    code: "OP_ROTATE_SCREEN",
    group: "Operational",
  },
  {
    id: 11,
    perm: "Allow technician to restart the vending machine.",
    code: "OP_RESTART_MACHINE",
    group: "Operational",
  },
  {
    id: 12,
    perm: "Allow technicians to open the vending machine location on map.",
    code: "OP_OPEN_MAP",
    group: "Operational",
  },
  {
    id: 13,
    perm: "Allow technician to enable or disable Maintenance Mode on the vending machine.",
    code: "OP_MAINTENANCE_MODE",
    group: "Operational",
  },
  {
    id: 14,
    perm: "Allow technician to access Auto Pay controls on the vending machine.",
    code: "OP_AUTO_PAY",
    group: "Operational",
  },
  {
    id: 22,
    perm: "Allow technicians to create transfer orders.",
    code: "OP_TRANSFER_ORDER",
    group: "Operational",
  },
  {
    id: 23,
    perm: "Allow technicians to tally orders.",
    code: "OP_TALLY_ORDER",
    group: "Operational",
  },
  {
    id: 25,
    perm: "Allow technician to reset hardware.",
    code: "OP_RESET_HARDWARE",
    group: "Operational",
  },
  {
    id: 26,
    perm: "Allow technician to configure location of the Vending Machine.",
    code: "OP_LOCATION_CONFIG",
    group: "Operational",
  },

  {
    id:27,
    perm: "Allow technician to create and execute Spare Parts Order.",
    code: "OP_SPARE_PARTS_ORDER",
    group: "Operational",
  }
];

export const FullRefillerPermissions = [
  {
    id: 1,
    perm: "Refill Products",
    group: "Refill",
  },
  {
    id: 2,
    perm: "Add/ Delete products in the refill plan",
    group: "Refill",
  },
  {
    id: 3,
    perm: "Swap products in the refill plan",
    group: "Refill",
  },
  {
    id: 4,
    perm: "Block/ unblock motors in the refill plan",
    group: "Refill",
  },
  {
    id: 5,
    perm: "Unlock/ Lock product of planogram via refill plan",
    group: "Refill",
  },
  {
    id: 6,
    perm: "Change depth of machine in the refill plan",
    group: "Refill",
  },
  {
    id: 7,
    perm: "Set expiry of a product in the refill plan",
    group: "Refill",
  },
  {
    id: 8,
    perm: "Get secret code for a machine to do free vend & change settings",
    group: "Machine",
  },
  {
    id: 9,
    perm: "Maintenance mode on/off setting",
    group: "Machine",
  },
  {
    id: 10,
    perm: "Refill mode on/off setting",
    group: "Machine",
  },
  {
    id: 11,
    perm: "Restart machine setting",
    group: "Machine",
  },
  {
    id: 12,
    perm: "See sales data of machine",
    group: "Machine",
  },
  {
    id: 13,
    perm: "Request Product",
    group: "Other",
  },
  {
    id: 14,
    perm: "Transfer Order",
    group: "Other",
  },
  {
    id: 15,
    perm: "Request Order",
    group: "Other",
  },
  {
    id: 16,
    perm: "Direct Sale",
    group: "Other",
  },
  {
    id: 17,
    perm: "Change PIN",
    group: "Other",
  },
  {
    id: 24,
    perm: "Allow refill partner to reassign refill plan on clicking full",
    group: "Refill",
  },
];

export const RefillerPermissionType = [
  "Custom",
  "Restricted",
  "Essential",
  "Standard",
  "Full",
];

export const TechnicianPermissionType = ["Default", "Full", "Custom"];

export const FOOD_TYPES = [
  { key: "VEG", label: "Veg", color: "#2e7d32" }, // green
  { key: "NON_VEG", label: "Non Veg", color: "#d32f2f" }, // red
  { key: "EGGS", label: "Eggs", color: "#fbc02d" }, // yellow
];
export const INCIDENT_PRIORITY = {
  1: "CRITICAL",
  2: "HIGH",
  3: "MEDIUM",
  4: "LOW",
};

export const INCIDENT_STATUS = {
  1: "CREATED",
  2: "IN_PROGRESS",
  3: "RESOLVED",
  // 4: "CLOSED",
};
export const TASK_STATUS = {
  1: "CREATED",
  2: "IN_PROGRESS",
  3: "PARTIALLY DONE",
  4: "COMPLETED",
  5: "REJECT",
  6: "REASSIGNED",
};
export const JOB_STATUS = {
  1: "CREATED",
  2: "IN PROGRESS",
  3: "COMPLETED",
  // 4: "COMPLETED",
  // 4: "UNASSIGN" // Uncomment if needed later
};

// Helper function to check if two arrays of permissions are exactly the same
export const isExactMatch = (permissions1, permissions2) => {
  // Check if both arrays have the same length
  if (permissions1.length !== permissions2.length) {
    return false;
  }

  // Check if every permission in permissions1 is present in permissions2
  return permissions1.every((perm1) => {
    // Find a matching permission in permissions2
    const matchingPerm = permissions2.find(
      (perm2) =>
        perm1.id === perm2.id &&
        perm1.perm === perm2.perm &&
        perm1.group === perm2.group,
    );
    // If a matching permission is not found, return false
    if (!matchingPerm) {
      return false;
    }
    // Remove the matched permission from permissions2
    const index = permissions2.indexOf(matchingPerm);
    permissions2.splice(index, 1);
    // Continue checking other permissions in permissions1
    return true;
  });
};

//to know the refiller permission type base don permission array data
export const VerifyPermissionType = (stockPermissionData) => {
  let matchedPermissionType = "";
  if (isExactMatch(stockPermissionData, RestrictedRefillPermissions)) {
    matchedPermissionType = RefillerPermissionType[1]; // Restricted
  } else if (isExactMatch(stockPermissionData, EssentialRefillPermissions)) {
    matchedPermissionType = RefillerPermissionType[2]; // Essential
  } else if (isExactMatch(stockPermissionData, StandardRefillPermissions)) {
    matchedPermissionType = RefillerPermissionType[3]; // Standard
  } else if (isExactMatch(stockPermissionData, FullRefillerPermissions)) {
    matchedPermissionType = RefillerPermissionType[4]; // Full
  } else {
    matchedPermissionType = "Custom";
  }
  return matchedPermissionType;
};

export const ReportNames = {
  planogramAdherence: "Planogram Adherence",
  refillFrequency: "Refill Frequency",
  uptime: "Machine Uptime",
  incident: "Incident",
  machineState: "Individual Machine State",
  machineLevelPlanogramAdherence: "Machine-Level Planogram Adherence",
  bulkPlanogramAdherence: "Bulk Planogram Adherence",
  refillAdherence: "Refill Adherence Report",
  financeReport: "Finance Report",
  refillAudit: "Refill Audit Report",
  itemWiseSales: "Item Wise Sales Report",
  itemDayMachineSales: "Item, Day, Machine Wise Sales Report",
  itemWiseMachineSales: "Item Wise Machine Wise Sales Report",
  machineSnapshot: "Machine Snapshot Report",
  dayWiseMachinePerformance: "Day Wise Machine Performance Report",
  machinePerformance: "Machine Performance Report",
};
export const SOURCE_TYPE = {
  TECHNICIAN_APP: "Technician App",
  REFILL_PARTNER_APP: "Refill Partner App",
  OPERATOR_APP: "Operator App",
  ERP: "ERP",
};

export const SOURCE_TYPE_BY_ID = {
  1: "Technician App",
  2: "Refill Partner App",
  3: "Operator App",
  4: "ERP",
};

export const TECHNICIAN_ACTION_TYPES = {
  ROTATE_SINGLE_MOTOR: "Rotate Single Motor",
  ROTATE_MOTOR: "Rotate Motor",
  RESET_ALL_MOTORS: "Reset All Motors",
  CONFIGURE_BOARD: "Configure Board",
  RESET_BOARD: "Reset Board",
  ROTATE_ALL_MOTORS: "Rotate All Motors",
  MARK_MOTOR_FAULTY: "Mark Motor Faulty",
  UNMARK_MOTOR_FAULTY: "Unmark Motor Faulty",
  SET_TEMPERATURE: "Set Temperature",
  GET_TEMPERATURE: "Get Temperature",
  ENABLE_AUTOPAY: "Enable Autopay",
  DISABLE_AUTOPAY: "Disable Autopay",
  ENABLE_MAINTENANCE: "Enable Maintenance",
  DISABLE_MAINTENANCE: "Disable Maintenance",
  BLOCK_MOTOR: "Block Motor",
  UNBLOCK_MOTOR: "Unblock Motor",
  RESTART_VENDING_MACHINE: "Restart Vending Machine",
  GET_MAC_ADDRESS: "Get MAC Address",
  GET_SECRET_CODE: "Get Secret Code",
  UNLINK_HARDWARE_BOARD: "Unlink Hardware Board",
  RESET_HARDWARE_BOARD: "Reset Hardware Board",
  ROTATE_SCREEN: "Rotate Screen",
  CONFIGURE_LOCATION: "Location Config",
};

//   export const TECHNICIAN_ACTION_COLOR_MAP = {
//   ROTATE_SINGLE_MOTOR: {
//     bg: "#E3F2FD",
//     color: "#1565C0",
//   },
//   ROTATE_MOTOR: {
//     bg: "#E3F2FD",
//     color: "#1565C0",
//   },
//   ROTATE_ALL_MOTORS: {
//     bg: "#E3F2FD",
//     color: "#1565C0",
//   },

//   CONFIGURE_BOARD: {
//     bg: "#FFF3E0",
//     color: "#EF6C00",
//   },
//   SET_TEMPERATURE: {
//     bg: "#FFF3E0",
//     color: "#EF6C00",
//   },

//   RESET_ALL_MOTORS: {
//     bg: "#FDECEA",
//     color: "#C62828",
//   },
//   RESET_BOARD: {
//     bg: "#FDECEA",
//     color: "#C62828",
//   },
//   RESET_HARDWARE_BOARD: {
//     bg: "#FDECEA",
//     color: "#C62828",
//   },
//   RESTART_VENDING_MACHINE: {
//     bg: "#FDECEA",
//     color: "#C62828",
//   },

//   MARK_MOTOR_FAULTY: {
//     // bg: "#FCE4EC",
//     // color: "#AD1457",
//     bg: "#FDECEA",
//     color: "#B71C1C",
//   },
//   UNMARK_MOTOR_FAULTY: {
//     bg: "#E8F5E9",
//     color: "#2E7D32",
//   },

//   ENABLE_AUTOPAY: {
//     bg: "#E8F5E9",
//     color: "#2E7D32",
//   },
//   DISABLE_AUTOPAY: {
//     bg: "#FDECEA",
//     color: "#C62828",
//   },

//   ENABLE_MAINTENANCE: {
//     bg: "#E8F5E9",
//     color: "#2E7D32",
//   },
//   DISABLE_MAINTENANCE: {
//     bg: "#FDECEA",
//     color: "#C62828",
//   },

//   BLOCK_MOTOR: {
//     bg: "#FDECEA",
//     color: "#B71C1C",
//   },
//   UNBLOCK_MOTOR: {
//     bg: "#E8F5E9",
//     color: "#1B5E20",
//   },

//   GET_TEMPERATURE: {
//     bg: "#E1F5FE",
//     color: "#0277BD",
//   },
//   GET_MAC_ADDRESS: {
//     bg: "#E1F5FE",
//     color: "#0277BD",
//   },
//   GET_SECRET_CODE: {
//     bg: "#E1F5FE",
//     color: "#0277BD",
//   },

//   UNLINK_HARDWARE_BOARD: {
//     bg: "#F3E5F5",
//     color: "#6A1B9A",
//   },
// };

export const TECHNICIAN_ACTION_COLOR_MAP = {
  // ROTATION (neutral blue-grey)
  ROTATE_SINGLE_MOTOR: {
    bg: "#F6F8FA",
    color: "#334155",
  },
  ROTATE_MOTOR: {
    bg: "#F6F8FA",
    color: "#334155",
  },
  ROTATE_ALL_MOTORS: {
    bg: "#F6F8FA",
    color: "#334155",
  },

  // CONFIG / CHANGE (warm grey)
  CONFIGURE_BOARD: {
    bg: "#FAFAF9",
    color: "#57534E",
  },
  SET_TEMPERATURE: {
    // bg: "#FAFAF9",
    // color: "#57534E",
    bg: "#F6FAF7",
    color: "#14532D",
  },

  // RESET / RESTART (soft warning grey)
  RESET_ALL_MOTORS: {
    bg: "#FAF7F7",
    color: "#7F1D1D",
  },
  RESET_BOARD: {
    bg: "#FAF7F7",
    color: "#7F1D1D",
  },
  RESET_HARDWARE_BOARD: {
    bg: "#FAF7F7",
    color: "#7F1D1D",
  },
  RESTART_VENDING_MACHINE: {
    bg: "#FAF7F7",
    color: "#7F1D1D",
  },

  // FAULT MARKING (very subtle red)
  MARK_MOTOR_FAULTY: {
    bg: "#FBF6F6",
    color: "#8B1D1D",
  },
  UNMARK_MOTOR_FAULTY: {
    bg: "#F6FAF7",
    color: "#14532D",
  },

  UNLINK_HARDWARE_BOARD: {
    bg: "#FBF6F6",
    color: "#8B1D1D",
  },

  // ENABLE / DISABLE (muted success / danger)
  ENABLE_AUTOPAY: {
    bg: "#F6FAF7",
    color: "#14532D",
  },
  DISABLE_AUTOPAY: {
    bg: "#FBF6F6",
    color: "#7F1D1D",
  },

  ENABLE_MAINTENANCE: {
    bg: "#F6FAF7",
    color: "#14532D",
  },
  DISABLE_MAINTENANCE: {
    bg: "#FBF6F6",
    color: "#7F1D1D",
  },

  // BLOCKING (serious action)
  BLOCK_MOTOR: {
    bg: "#FBF6F6",
    color: "#7F1D1D",
  },
  UNBLOCK_MOTOR: {
    bg: "#F6FAF7",
    color: "#14532D",
  },

  // GET / READ (cool neutral)
  GET_TEMPERATURE: {
    bg: "#F7F9FC",
    color: "#1E3A8A",
  },
  GET_MAC_ADDRESS: {
    bg: "#F7F9FC",
    color: "#1E3A8A",
  },
  GET_SECRET_CODE: {
    bg: "#F7F9FC",
    color: "#1E3A8A",
  },
};

export const STATUS_COLOR_MAP = {
  SUCCESS: {
    // bg: "#E8F5E9",   // light green
    bg: "#F6FAF7",
    color: "#2E7D32", // dark green
  },
  FAILED: {
    // bg: "#FDECEA",   // light red
    bg: "#FBF6F6",
    color: "#C62828", // dark red
  },
   DEFAULT: {
    bg: "#FFF8E1",   // light yellow
    color: "#F9A825", // dark yellow / amber
  },
};

export const SOURCE_TYPE_COLOR_MAP = {
  TECHNICIAN_APP: {
    bg: "#F4F8FB", // soft steel blue
    color: "#1F3A5F", // deep navy
    border: "#C6D4E1",
  },

  1: {
    // TECHNICIAN_APP (numeric)
    bg: "#F4F8FB",
    color: "#1F3A5F",
    border: "#C6D4E1",
  },

  REFILL_PARTNER_APP: {
    bg: "#F6F9F6", // soft sage
    color: "#2F4F3E", // dark forest
    border: "#CBD8CE",
  },

  2: {
    // REFILL_PARTNER_APP
    bg: "#F6F9F6",
    color: "#2F4F3E",
    border: "#CBD8CE",
  },

  OPERATOR_APP: {
    bg: "#FAF7F2", // warm neutral
    color: "#5A3E1B", // muted brown
    border: "#E0D6C8",
  },

  3: {
    // OPERATOR_APP
    bg: "#FAF7F2",
    color: "#5A3E1B",
    border: "#E0D6C8",
  },

  ERP: {
    bg: "#F5F6F8", // system gray
    color: "#2E3440", // charcoal
    border: "#D1D5DB",
  },

  4: {
    // ERP
    bg: "#F5F6F8",
    color: "#2E3440",
    border: "#D1D5DB",
  },
};

export const SECTION_CONFIGS = {
  "customer-experience": {
    id: "customer_experience",
    title: "Customer Experience",
    // With Display Type = "No Display" (Hardware & Machine Setup) there is no
    // customer-facing screen, so these experience settings don't apply — grey
    // the whole section out (and skip its validation on save).
    disableAllWhen: { field: "displayType", equalsName: "No Display" },
    formSections: [
      {
        id: "customer_detail_prompts",
        title: "Customer Detail Prompts",
        description:
          "Configure what customer information is collected during checkout and what prompt messages are shown.",
        fields: [
          {
            name: "collectCustomerDetailsVia",
            type: "checkbox-group",
            label: "Collect Customer Details Via",
            tooltipTitle:
              "Choose which contact details the machine collects from customers.",
            options: [
              { key: "phoneNumberPrompt", label: "Phone" },
              { key: "emailPrompt", label: "Email" },
              {
                key: "customPrompt",
                label: "Custom",
                tooltipTitle:
                  "Collect a custom detail (e.g. Employee ID) using your own field label.",
              },
            ],
          },
          {
            name: "promptTitle",
            type: "text",
            label: "Customer Details Prompt Title",
            placeholder: "e.g. Enter your details",
            tooltipTitle: "Heading shown on the customer-details prompt.",
          },
          {
            name: "promptMessage",
            type: "text",
            label: "Customer Details Prompt Message",
            placeholder: "e.g. We'll send the receipt to you",
            tooltipTitle: "Message shown under the customer-details prompt.",
          },
          {
            name: "customFieldTitle",
            type: "text",
            label: "Custom Field Title",
            placeholder: "e.g. Employee ID",
            tooltipTitle:
              "Label for the custom detail collected from the customer.",
            // Full-width row so the Skip toggle below reflows down when shown.
            fullWidth: true,
            // Only visible when the "Custom" option is checked.
            showWhen: { field: "customPrompt", equals: true },
          },
          {
            name: "skipCustomerDetails",
            type: "toggle",
            label: "Skip Customer Details Option",
            fullWidth: true,
            tooltipTitle:
              "Allow customers to skip entering their details at checkout.",
          },
        ],
      },
      {
        id: "feature_toggles",
        title: "Feature Toggles",
        description:
          "Show or hide specific features on the machine's customer-facing interface.",
        layout: "toggle-list",
        fields: [
          {
            name: "showProductRequest",
            type: "toggle",
            label: "Show Product Request",
            tooltipTitle:
              "Let customers request products that are not currently stocked.",
          },
          {
            name: "showPickupButton",
            type: "toggle",
            label: "Show Pickup Button",
            tooltipTitle: "Show a pickup button on the dispense screen.",
          },
          {
            name: "showInvoiceOnDispenseScreen",
            type: "toggle",
            label: "Show Invoice on Dispense Screen",
            tooltipTitle: "Display the invoice on the dispense screen.",
          },
          {
            name: "showFeedbackSection",
            type: "toggle",
            label: "Show Feedback Section",
            tooltipTitle: "Show the feedback section to customers.",
          },
          {
            name: "feedbackSectionTitle",
            type: "text",
            label: "Feedback Section Title",
            placeholder: "Please Share Your Feedback Below",
            tooltipTitle: "Heading shown above the feedback section.",
            fullWidth: true,
            // Only relevant when the feedback section is enabled.
            showWhen: { field: "showFeedbackSection", equals: true },
          },
          {
            name: "showCustomerFeedbackCommentSection",
            type: "toggle",
            label: "Show Customer Feedback Comment Section",
            tooltipTitle: "Allow customers to leave a free-text comment.",
            // Only available when the feedback section is enabled.
            disabledWhen: { field: "showFeedbackSection", equals: true },
          },
          {
            name: "showFeedbackImage",
            type: "toggle",
            label: "Show Feedback Image",
            tooltipTitle: "Show an image alongside the feedback section.",
            // Only available when the feedback section is enabled.
            disabledWhen: { field: "showFeedbackSection", equals: true },
          },
          {
            name: "showPriceOfProducts",
            type: "toggle",
            label: "Show Price of Products",
            tooltipTitle: "Display product prices on the customer screen.",
          },
          {
            name: "showCartTotalAmount",
            type: "toggle",
            label: "Show Cart Total Amount",
            tooltipTitle: "Display the cart total on the customer screen.",
          },
        ],
      },
      {
        id: "cart_timeout_settings",
        title: "Cart & Timeout Settings",
        description:
          "Configure all session timings, cart limits, and post-dispense behaviors.",
        fields: [
          {
            name: "screenInactivityTimeout",
            type: "number",
            label: "Screen Inactivity Timeout",
            placeholder: "Seconds",
            note: "Seconds",
            min: 0,
            allowDecimals: false,
            tooltipTitle:
              "Time (in seconds) of inactivity before the screen resets.",
          },
          {
            name: "cartResetTimeout",
            type: "number",
            label: "Cart Reset Timeout",
            placeholder: "Seconds",
            note: "Seconds",
            min: 0,
            allowDecimals: false,
            tooltipTitle:
              "Time (in seconds) before an idle cart is reset.",
          },
          {
            name: "paymentTimeout",
            type: "number",
            label: "Payment Timeout",
            placeholder: "Seconds",
            note: "Seconds",
            min: 0,
            allowDecimals: false,
            tooltipTitle: "Time (in seconds) allowed to complete payment.",
          },
          {
            name: "postDispenseTimeout",
            type: "number",
            label: "Post Dispense Timeout",
            placeholder: "Seconds",
            note: "Seconds",
            min: 0,
            allowDecimals: false,
            tooltipTitle:
              "Time (in seconds) the dispense screen stays after vending.",
          },
          {
            name: "maxItemsInCart",
            type: "number",
            label: "Max Items in Cart",
            placeholder: "Quantity",
            note: "Quantity",
            min: 0,
            allowDecimals: false,
            tooltipTitle: "Maximum total items a customer can add to the cart.",
          },
          {
            name: "maxUniqueItemsPerCart",
            type: "number",
            label: "Max Unique Items Per Cart",
            placeholder: "Quantity",
            note: "Quantity",
            min: 0,
            allowDecimals: false,
            tooltipTitle:
              "Maximum number of distinct products a customer can add.",
          },
          {
            name: "maxCartAmount",
            type: "number",
            label: "Max Cart Amount",
            placeholder: "₹ (Inr)",
            note: "₹ (Inr)",
            min: 0,
            allowDecimals: false,
            tooltipTitle: "Maximum total cart value allowed.",
          },
          {
            name: "unlockTime",
            type: "number",
            label: "Unlock Time",
            placeholder: "Minutes",
            note: "Minutes",
            min: 0,
            allowDecimals: false,
            tooltipTitle: "Time (in minutes) the machine stays unlocked.",
          },
          {
            name: "isDispenseLaterAllowed",
            type: "toggle",
            label: "Is Dispense Later Allowed",
            fullWidth: true,
            tooltipTitle:
              "Allow customers to dispense purchased items at a later time.",
          },
          {
            // All dispense-later settings are a sub-section of the toggle above.
            type: "group",
            indent: true,
            showWhen: { field: "isDispenseLaterAllowed", equals: true },
            fields: [
              {
                name: "dispenseLaterTimeout",
                type: "number",
                label: "Dispense Later Timeout",
                placeholder: "Minutes",
                note: "Minutes",
                min: 0,
                allowDecimals: false,
                tooltipTitle:
                  "Time (in minutes) a customer has to dispense items later.",
              },
              {
                name: "userCancellationTime",
                type: "number",
                label: "User Cancellation Time",
                placeholder: "Minutes",
                note: "Minutes",
                min: 0,
                allowDecimals: false,
                tooltipTitle:
                  "Time (in minutes) within which a customer can cancel for a full refund.",
              },
              {
                name: "itemBlocking",
                type: "toggle",
                label: "Item Blocking",
                fullWidth: true,
                tooltipTitle:
                  "Block the item in the machine until the customer dispenses or cancels.",
              },
              // Item Blocking sub-section (Refund Settings + Refund Percentage)
              // is hidden — feature under development. Toggling Item Blocking to
              // Yes shows an "under development" toast (see MainSection).
              /*
              {
                name: "refundSetting",
                type: "radio-group",
                label: "Refund Settings",
                indent: true,
                tooltipTitle:
                  "How refunds are handled when a customer cancels after the cancellation time.",
                options: [
                  { value: "no_refund", label: "No Refund" },
                  { value: "full_refund", label: "Full Refund" },
                  { value: "custom_refund", label: "Custom Refund" },
                ],
                showWhen: { field: "itemBlocking", equals: true },
              },
              {
                name: "refundPercentage",
                type: "slider",
                label: "Refund Percentage",
                indent: true,
                min: 10,
                max: 75,
                step: 1,
                unit: "%",
                marks: [
                  { value: 10, label: "10%" },
                  { value: 25, label: "25%" },
                  { value: 50, label: "50%" },
                  { value: 75, label: "75%" },
                ],
                tooltipTitle:
                  "Percentage of the amount refunded for a custom refund.",
                showWhen: { field: "refundSetting", equals: "custom_refund" },
              },
              */
              {
                name: "cancellationLogic",
                type: "info",
                title: "Cancellation logic:",
                text: "If a customer cancels within the User Cancellation Time → full refund is issued automatically. If a customer cancels after the User Cancellation Time and Item Blocking is ON → refund is applied as per the Refund Settings above.",
              },
            ],
          },
        ],
      },
    ],
  },

  "location-client": {
    id: "location_and_client",
    title: "Location & Client",
    formSections: [
      {
        title: "Geographic Information",
        icon: <LocationIcon isActive={true} />,
        fields: [
          {
            name: "region",
            label: "Region",
            required: true,
            placeholder: "Select region",
            tooltipTitle:
              "Broad regional grouping used for operational reporting.",
          },
          {
            name: "state",
            label: "State",
            required: true,
            placeholder: "Select state",
            tooltipTitle:
              "Administrative state where the machine is installed.",
          },
          {
            name: "city",
            label: "City",
            required: true,
            placeholder: "Select city",
            tooltipTitle: "City or urban location of the machine.",
          },
          {
            name: "area",
            label: "Area",
            placeholder: "Select area",
            tooltipTitle:
              "Area or zone where the machine is physically placed.",
          },
          {
            name: "cafeteria",
            label: "Cafeteria",
            placeholder: "Select cafeteria",
            tooltipTitle:
              "Exact physical location where the machine is deployed.",
          },
          {
            name: "locationCategory",
            label: "Location Category",
            placeholder: "Select location category",
            tooltipTitle:
              "Type of location where machine is installed (e.g., Office, Factory, Hospital, College).",
          },
        ],
      },
      {
        id: "business_relationships",
        title: "Business Relationships",
        icon: <MultiUserIcon isActive={true} />,
        fields: [
          {
            name: "businessUnit",
            label: "Business Unit",
            placeholder: "Select business unit",
            tooltipTitle:
              "Internal business division responsible for this machine.",
          },
          {
            name: "channelPartner",
            label: "Channel Partner",
            placeholder: "Select channel partner",
            tooltipTitle:
              "Commercial or operational partner managing the machine.",
          },
          {
            name: "client",
            label: "Client",
            placeholder: "Select client",
            tooltipTitle: "Primary client organization",
          },
          {
            name: "endClient",
            label: "End Client",
            placeholder: "Select end client",
            tooltipTitle:
              "Legal entity operating the site where the machine is installed.",
          },
          {
            name: "paymentType",
            label: "Payment Type",
            multi_select: true,
            placeholder: "Select payment type(s)",
            tooltipTitle: "Payment modes supported on this machine.",
          },
          {
            name: "machineType",
            label: "Machine Type",
            placeholder: "Select Machine type",
            tooltipTitle:
              "Machine Type defines the type of the vending machine based on the products it is designed to dispense. Example: Snacks, Beverages, Combo.",
          },
        ],
      },
      {
        id: "location_detail",
        title: "Location Detail",
        icon: <MapIcon isActive={true} />,
        fields: [
          {
            name: "machineAddress",
            label: "Machine Address",
            type: "text",
            placeholder: "Enter machine address",
            tooltipTitle: "Complete physical address of the machine location.",
          },
          {
            name: "latitude",
            type: "text",
            label: "Machine Latitude",
            placeholder: "Enter latitude",
            tooltipTitle: "Manual GPS latitude of the machine location.",
          },
          {
            name: "longitude",
            type: "text",
            label: "Machine Longitude",
            placeholder: "Enter longitude",
            tooltipTitle: "Manual GPS longitude of the machine location.",
          },
          {
            name: "buildingLatitude",
            type: "text",
            label: "Building Latitude",
            placeholder: "Enter building latitude",
            tooltipTitle: "Manual GPS latitude of the building location.",
          },
          {
            name: "buildingLongitude",
            type: "text",
            label: "Building Longitude",
            placeholder: "Enter building longitude",
            tooltipTitle: "Manual GPS longitude of the building location.",
          },
          {
            name: "buildingToMachineTime",
            type: "text",
            label: "Building to Machine Time (Minutes)",
            placeholder: "Enter time in minutes",
            tooltipTitle:
              "Estimated time required to reach the machine from the building entrance.",
          },
        ],
      },
    ],
  },

  "machine-identity": {
    id: "machine_identity",
    title: "Machine Identity",
    formSections: [
      {
        fields: [
          {
            name: "machinePlatform",
            type: "dropdown",
            label: "Machine Platform",
            required: true,
            placeholder: "Select platform",
            tooltipTitle: "Select the platform this machine runs on.",
            // Options are loaded from the backend (action "list.machine_platforms")
            // and matched by label in listOfMasterType. See MainSection prefill.
            options: [],
          },
          {
            name: "machineDisplayName",
            type: "text",
            label: "Machine Display Name",
            required: true,
            placeholder: "Enter machine display name",
            tooltipTitle: "Enter the user-facing name for this machine.",
          },
          {
            name: "machineSystemName",
            type: "text",
            label: "Machine System Name",
            placeholder: "Enter machine system name",
            tooltipTitle: "Enter the system name used internally.",
          },
          {
            name: "machineExternalId",
            type: "text",
            label: "Common Machine ID",
            required: true,
            placeholder: "Enter common machine ID",
            tooltipTitle: "Enter the common identifier used for integrations.",
          },
          {
            name: "machineThirdPartyAppName",
            type: "text",
            label: "Machine Third-Party App Name",
            placeholder: "Enter third-party app name",
            tooltipTitle: "Enter the name used in third-party applications.",
          },
        ],
      },
      {
        // Display-only block: state is local to the component and is never
        // sent to the API. Provider lists / instance options are static.
        type: "custom",
        component: "machine-configuration",
        componentProps: {
          title: "Machine Configuration",
          description:
            "Define integrations and invoice settings for this machine.",
          instanceOption: "Instance",
          toggles: [
            {
              key: "purchaseOrders",
              label: "Machine Purchase Orders",
              tooltipTitle:
                "Enable purchase order integrations for this machine.",
              providers: ["Sales Code", "Retailfy"],
            },
            {
              key: "salesInvoices",
              label: "Machine Sales Invoices",
              tooltipTitle:
                "Enable sales invoice integrations for this machine.",
              providers: ["SalesCode", "Retailify"],
            },
          ],
        },
        fields: [],
      },
    ],
  },

  "ownership-mapping": {
    id: "ownership_mapping",
    title: "Ownership Mapping",
    formSections: [
      {
        title: "Ownership Details",
        icon: <MultiUserIcon isActive={true} />,
        fields: [
          {
            name: "machineOwnedBy",
            label: "Machine Owned By",
            required: true,
            placeholder: "Select machine owner",
            tooltipTitle: "Select the legal owner of the vending machine",
          },
          {
            name: "managedBy",
            label: "Managed By",
            required: true,
            placeholder: "Select manager",
            tooltipTitle:
              "Select the entity responsible for managing the machine.",
          },
          {
            name: "maintenanceBy",
            label: "Machine Maintenance By",
            placeholder: "Select maintenance provider",
            tooltipTitle:
              "Select the company responsible for machine maintenance.",
          },
          {
            name: "maintenanceStartDate",
            label: "Maintenance Start Date",
            type: "date",
            placeholder: "Select start date",
            tooltipTitle: "Select the maintenance contract start date.",
          },
          {
            name: "maintenanceEndDate",
            label: "Maintenance End Date",
            type: "date",
            placeholder: "Select end date",
            tooltipTitle: "Select the maintenance contract end date.",
          },
          {
            name: "defaultTechnician",
            label: "Default Technician",
            placeholder: "Select default technician",
            tooltipTitle:
              "Select the default technician assigned to this machine.",
          },
          {
            name: "insuranceBy",
            label: "Machine Insurance By",
            placeholder: "Select insurance provider",
            tooltipTitle: "Select the insurance provider for this machine.",
          },
          {
            name: "insuranceStartDate",
            label: "Insurance Start Date",
            type: "date",
            placeholder: "Select start date",
            tooltipTitle: "Select the insurance policy start date.",
          },
          {
            name: "insuranceEndDate",
            label: "Insurance End Date",
            type: "date",
            placeholder: "Select end date",
            tooltipTitle: "Select the insurance policy end date.",
          },
          {
            name: "machineWarrantyBy",
            label: "Machine Warranty By",
            placeholder: "Select warranty provider",
            tooltipTitle:
              "Select the entity providing warranty for this machine.",
          },
          {
            name: "machineFinancedBy",
            label: "Machine Financed By",
            placeholder: "Select financing bank",
            tooltipTitle:
              "Select the financial institution or entity financing this machine.",
          },
          {
            name: "machineRegisteredUnder",
            label: "Machine Registered Under",
            placeholder: "Select registration entity",
            tooltipTitle:
              "Select the legal entity under which the machine is registered.",
          },
          {
            name: "machineDeployedAt",
            label: "Machine Deployed At",
            placeholder: "Select deployment location",
            tooltipTitle: "Select the location where the machine is deployed.",
          },
          {
            name: "ownershipType",
            label: "Ownership Type",
            placeholder: "Select ownership type",
            tooltipTitle: "Select the ownership model for this machine.",
          },
          {
            name: "ownershipStartDate",
            label: "Ownership Start Date",
            type: "date",
            placeholder: "Select start date",
            tooltipTitle: "Select the ownership contract start date.",
          },
          {
            name: "ownershipEndDate",
            label: "Ownership End Date",
            type: "date",
            placeholder: "Select end date",
            tooltipTitle: "Select the ownership contract end date.",
          },
        ],
      },
    ],
  },

  "installation-contract": {
    id: "installation_and_contract",
    title: "Installation & Contract",
    formSections: [
      {
        title: "Installation Details",
        icon: <LocationIcon isActive={true} />,
        fields: [
          {
            name: "installationDate",
            label: "Installation Date",
            type: "date",
            required: true,
            placeholder: "Select installation date",
            tooltipTitle:
              "Date on which the machine was installed at the site.",
          },
          {
            name: "contractStartDate",
            label: "Contract Start Date",
            type: "date",
            placeholder: "Select contract start date",
            tooltipTitle: "Date on which the commercial contract starts.",
          },
          {
            name: "contractEndDate",
            label: "Contract End Date",
            type: "date",
            placeholder: "Select contract end date",
            tooltipTitle: "Date on which the commercial contract expires.",
          },
        ],
      },
    ],
  },

  "refill-configuration": {
    id: "refill_configuration",
    title: "Refill Configuration",
    // Enabling the inventory-less model greys out every other field and the
    // Refill Schedule in this section — the machine runs without tracked
    // inventory, so refill settings don't apply. The toggle itself stays
    // enabled so the model can be switched back off.
    disableAllWhen: {
      field: "enableInventoryLess",
      exceptFields: ["enableInventoryLess"],
    },
    formSections: [
      {
        title: "Refill Settings",
        icon: <LocationIcon isActive={true} />,
        // Two-column layout (grid auto-flows row-major). Toggles use the box
        // variant with the switch right-aligned (toggleAlign). Warehouse fields
        // are labelled "Dark Store" — their options still come from Warehouses
        // (listOfMasterType names are kept in sync in MainSection).
        fields: [
          {
            name: "enableInventoryLess",
            type: "toggle",
            label: "Enable Inventory-less Model",
            toggleAlign: "right",
            tooltipTitle: "Run this machine without tracked inventory.",
          },
          {
            name: "machineStorage",
            type: "toggle",
            label: "Machine Storage",
            toggleAlign: "right",
            tooltipTitle: "Whether this machine has on-site storage.",
          },
          // Revealed only when Machine Storage = Yes.
          {
            name: "machineStorageName",
            type: "text",
            label: "Machine Storage Name",
            placeholder: "e.g. Left Storage Unit",
            tooltipTitle: "A name to identify this machine's on-site storage.",
            showWhen: { field: "machineStorage" },
          },
          {
            name: "storageType",
            type: "radio-group",
            label: "Storage Type",
            halfWidth: true,
            tooltipTitle: "Whether the storage is internal or external.",
            options: [
              { value: "internal", label: "Internal" },
              { value: "external", label: "External" },
            ],
            showWhen: { field: "machineStorage" },
          },
          // Shown only when Storage Type = External. Options are injected at runtime
          // by MainSection via listOfMasterType: Channel = partner brand codes,
          // Instance = the Retailify clients under that brand (warehouse_platform_id).
          // Persisted as storage_channel_id (brand code) / storage_instance_id (wp id).
          {
            name: "storageChannel",
            type: "dropdown",
            label: "Channel",
            placeholder: "Select channel",
            tooltipTitle: "External storage channel.",
            options: STORAGE_CHANNEL_OPTIONS,
            showWhen: { field: "storageType", equals: "external" },
          },
          {
            name: "storageInstance",
            type: "dropdown",
            label: "Instance",
            placeholder: "Select instance",
            tooltipTitle: "External storage instance.",
            options: STORAGE_INSTANCE_OPTIONS,
            showWhen: { field: "storageType", equals: "external" },
          },
          {
            name: "defaultWarehouse",
            label: "Default Dark Store",
            required: true,
            placeholder: "Select default dark store",
            tooltipTitle: "Primary dark store used for product refills.",
          },
          {
            name: "defaultRefillPartner",
            label: "Default Refill Partner",
            required: true,
            placeholder: "Select default refill partner",
            tooltipTitle:
              "Primary refiller responsible for servicing the machine.",
          },
          {
            name: "backupWarehouses",
            label: "Backup Dark Store (Multi-Select)",
            type: "multi-select",
            multi_select: true,
            placeholder: "Select backup dark stores",
            tooltipTitle:
              "Fallback dark stores used when the default is unavailable.",
          },
          {
            name: "backupRefillPartners",
            label: "Backup Refill Partner (Multi-Select)",
            type: "multi-select",
            multi_select: true,
            placeholder: "Select backup refill partners",
            tooltipTitle:
              "Backup refillers used if the primary refiller is unavailable.",
          },
          {
            name: "depotRefill",
            type: "toggle",
            label: "Depot Refill",
            toggleAlign: "right",
            tooltipTitle: "Whether this machine is refilled from a depot.",
          },
          {
            name: "stockSuppliedBy",
            label: "Stock Supplied By",
            placeholder: "Select stock supplier",
            tooltipTitle:
              "Select the vendor responsible for supplying stock to this machine",
          },
          {
            name: "refillingOwnership",
            label: "Refilling Ownership",
            required: true,
            placeholder: "Select refilling ownership",
            tooltipTitle: "Entity accountable for timely refilling.",
          },
          {
            name: "gracePeriodMinutes",
            label: "Grace Period (Minutes)",
            type: "number",
            placeholder: "Enter grace period in minutes",
            tooltipTitle:
              "Minutes after planned refill time during which refill is allowed.",
          },
          {
            name: "bufferPeriodMinutes",
            label: "Buffer Period (Minutes)",
            type: "number",
            placeholder: "Enter buffer period in minutes",
            tooltipTitle:
              "Minutes before planned refill time during which refill is allowed.",
          },
          {
            name: "machineToMachineTimeMinutes",
            label: "Machine to Machine Time (Minutes)",
            type: "number",
            placeholder: "Enter time in minutes",
            tooltipTitle:
              "Average time required to travel between two vending machines located within the same building or site.",
          },
        ],
        sectionInfoBanner: true,
        sectionInfoBannerOne: true,
        sectionInfoBannerOne: {
          message:
            "Backup entities are used when the default assignment is unavailable.",
        },

        sectionInfoBannerProp: {
          message:
            "Refill Time defines the planned refill time for the vending machine. Refill window is automatically calculated using Buffer Minutes and Grace Period.",
        },
      },
      {
        title: "Refill Schedule",
        icon: <CalendarIcon isActive={true} />,
        type: "custom",
        component: "day-time-scheduler",
        componentProps: {
          title: "Refill Schedule",
          // description:
          //   "Define daily operating hours during which the machine is expected to remain online.",
          // infoMessage: "Uptime must fall within the same calendar day.",
          icon: ClockIcon,
          mode: "slots",
          dataKey: "refillSchedule",
          tooltipTitle:
            "Enable only the days when refilling is expected. Disabled days will not be considered for refill SLA and no refill schedule will be created.",
        },
        fields: [],
      },
    ],
  },

  "machine-uptime": {
    id: "machine_uptime",
    title: "Machine Uptime",
    formSections: [
      {
        title: "Uptime Configuration",
        icon: <LocationIcon isActive={true} />,
        fields: [
          {
            name: "uptime_timezone",
            label: "Timezone",
            required: true,
            placeholder: "Select timezone",
            tooltipTitle: "Timezone used for uptime and SLA calculations.",
          },
          {
            name: "uptimeType",
            label: "Uptime Type",
            required: true,
            placeholder: "Select uptime type",
            options: ["24x7", "Custom"],
            tooltipTitle:
              "Select whether the machine operates 24x7 or on a custom schedule.",
          },
        ],
      },
      {
        title: "Machine Uptime Window",
        type: "custom",
        icon: <CalendarIcon isActive={true} />,
        component: "day-time-scheduler",
        componentProps: {
          title: "Machine Uptime Window",
          description:
            "Define daily operating hours during which the machine is expected to remain online.",
          infoMessage: "Uptime must fall within the same calendar day.",
          mode: "range",
          showApplySame: true,
          dataKey: "machineUptimeWindow",
          tooltipTitle:
            "Only one uptime window is allowed per day. If the machine does not operate on a particular day, keep that day disabled.",
        },
        fields: [],
      },
    ],
  },

  "hardware-setup": {
    id: "hardware_and_machine_setup",
    title: "Hardware & Machine Setup",
    // Wired full-stack. 5.1 options are loaded at runtime from
    // list.hardware_options and matched into listOfMasterType by the field
    // `label` (so the `options` arrays here stay empty). Saved via
    // create_update.vending_machine_hardware_setup (5.1 + VMC ports + 5.3
    // automation) and create_update.vending_machine_peripherals (5.2).
    // Field order follows the two-column grid in the design.
    formSections: [
      {
        title: "Device & Hardware",
        icon: <SettingsIcon isActive={true} />,
        description:
          "Configure the physical board type and device hardware for this machine.",
        fields: [
          {
            name: "motherboard",
            type: "dropdown",
            label: "Motherboard",
            requiredStar: true,
            placeholder: "Select platform",
            tooltipTitle: "Select the motherboard / board platform.",
            options: [],
          },
          {
            name: "networkDeviceType",
            type: "dropdown",
            label: "Network Device Type",
            requiredStar: true,
            multi_select: true,
            placeholder: "Select device type...",
            tooltipTitle: "Select the network connectivity device(s).",
            options: [],
          },
          {
            name: "displayType",
            type: "dropdown",
            label: "Display Type",
            placeholder: "Select display type",
            tooltipTitle: "Select the type of display fitted to the machine.",
            note: "Note: if no display is selected or an Android board is selected, a different build will be applied automatically. The machine detects the board type on startup and loads the appropriate configuration.",
            options: [],
          },
          {
            name: "displayBrand",
            type: "dropdown",
            label: "Display Brand",
            placeholder: "Select display brand",
            tooltipTitle: "Select the brand of the display.",
            options: [],
          },
          {
            name: "portSettings",
            type: "dropdown",
            label: "Port Settings",
            requiredStar: true,
            placeholder: "Select port settings",
            tooltipTitle: "Select the port configuration profile.",
            options: [],
          },
          {
            name: "vmcBoardType",
            type: "dropdown",
            label: "VMC Board Type",
            requiredStar: true,
            placeholder: "Select board type",
            tooltipTitle: "Select the vending machine controller board type.",
            options: [],
          },
          // ── Board-type specific config (shown only for the matching board) ──
          // JuiceTap:
          {
            name: "maxQuantityofJuicedOranges",
            type: "number",
            label: "Maximum Quantity of Juiced Oranges",
            placeholder: "e.g. 10",
            min: 0,
            showWhen: { field: "vmcBoardType", nameIncludes: "JuiceTap" },
            tooltipTitle: "Maximum number of oranges that can be juiced.",
          },
          {
            name: "shutdownTemperature",
            type: "number",
            label: "Shutdown Temperature",
            placeholder: "e.g. -5",
            allowNegative: true,
            showWhen: { field: "vmcBoardType", nameIncludes: "JuiceTap" },
            tooltipTitle: "Temperature at which the machine shuts down (may be negative).",
          },
          {
            name: "orangeJuiceWeight",
            type: "number",
            label: "Orange Juice Weight",
            placeholder: "e.g. 200",
            min: 0,
            showWhen: { field: "vmcBoardType", nameIncludes: "JuiceTap" },
            tooltipTitle: "Weight of orange juice produced.",
          },
          {
            name: "maxSqueezeTime",
            type: "number",
            label: "Maximum Squeeze Time",
            placeholder: "e.g. 30",
            min: 0,
            showWhen: { field: "vmcBoardType", nameIncludes: "JuiceTap" },
            tooltipTitle: "Maximum squeeze time.",
          },
          {
            name: "automaticCleaning",
            type: "toggle",
            label: "Automatic Cleaning",
            showWhen: { field: "vmcBoardType", nameIncludes: "JuiceTap" },
            tooltipTitle: "Enable scheduled automatic cleaning.",
          },
          {
            name: "cleaningSchedules",
            type: "cleaning-schedules",
            label: "Cleaning Schedules",
            // Visible only for JuiceTap AND when Automatic Cleaning is on.
            showWhen: [
              { field: "vmcBoardType", nameIncludes: "JuiceTap" },
              { field: "automaticCleaning" },
            ],
            tooltipTitle: "Up to 5 cleaning times, each with a duration in seconds.",
          },
          // EL980:
          {
            name: "el980LockerAfter",
            type: "number",
            label: "EL980 Locker Offset",
            placeholder: "-1",
            allowNegative: true,
            showWhen: { field: "vmcBoardType", nameIncludes: "EL980" },
            tooltipTitle:
              "EL980 locker offset (supports positive and negative values, default -1).",
          },
          // TCN Fridge:
          {
            name: "autoTallyMachineWeights",
            type: "toggle",
            label: "Auto Tally Machine Weights",
            showWhen: { field: "vmcBoardType", nameIncludes: "Fridge" },
            tooltipTitle: "Automatically tally machine weights.",
          },
          {
            name: "dropSensorAvailable",
            type: "toggle",
            label: "Drop Sensor Available",
            tooltipTitle: "Whether a drop sensor is installed on the machine.",
          },
          {
            name: "bypassVendsensor",
            type: "toggle",
            label: "Bypass Vendsensor",
            disabledWhen: { field: "dropSensorAvailable" },
            tooltipTitle:
              "Bypass the vend sensor (available only when a drop sensor is present).",
          },
          {
            name: "disputedTransactionHandling",
            type: "toggle",
            label: "Disputed Transaction Handling",
            tooltipTitle:
              "Enable automated handling of disputed transactions.",
          },
          {
            name: "vmBrandModel",
            type: "dropdown",
            label: "VM Brand & Model",
            placeholder: "Select brand & model",
            tooltipTitle: "Select the vending machine brand and model.",
            options: [],
          },
          {
            name: "typeOfMachine",
            type: "dropdown",
            label: "Type of Machine",
            placeholder: "Select machine type",
            tooltipTitle: "Select the type of vending machine.",
            options: [],
          },
          {
            name: "machineCoolingType",
            type: "dropdown",
            label: "Machine Cooling Type",
            placeholder: "Select cooling type",
            tooltipTitle: "Select the cooling system type.",
            options: [],
          },
          {
            name: "defaultTemperature",
            type: "number",
            label: "Default Temperature (in °C)",
            placeholder: "e.g. 4",
            tooltipTitle: "Default operating temperature in degrees Celsius.",
          },
          // Right-column spacer so the VMC port pair starts a fresh row.
          { type: "spacer" },
          // VMC serial ports — free-text device paths, only shown when Port
          // Settings = "Custom". Sent to the machine as vmcPort / vmcPort485.
          {
            name: "vmcPortRs232",
            type: "text",
            label: "VMC Port (RS232)",
            requiredStar: true,
            placeholder: "e.g. /dev/ttyUSB0",
            tooltipTitle: "Serial port the VMC communicates on (RS232).",
            showWhen: { field: "portSettings", equalsName: "Custom" },
          },
          {
            name: "vmcPort485",
            type: "text",
            label: "VMC Port 485 (RS485)",
            requiredStar: true,
            placeholder: "e.g. /dev/ttyUSB1",
            tooltipTitle: "Serial port the VMC communicates on (RS485).",
            showWhen: { field: "portSettings", equalsName: "Custom" },
          },
        ],
      },
      {
        type: "custom",
        component: "peripherals",
        // Controlled by formData.peripherals; brand/channel/instance/auth/
        // recharge options are loaded from list.peripheral_options and passed
        // in via the peripheralOptions prop. `type` is the backend
        // peripheral_type key.
        componentProps: {
          legend: "PERIPHERALS",
          statusOptions: [
            { id: "available", name: "Available" },
            { id: "not_applicable", name: "Not Applicable" },
          ],
          peripherals: [
            { key: "coinCollector", type: "coin_collector", label: "Coin Collector", tooltipTitle: "Whether a coin collector is installed on this machine." },
            { key: "billCollector", type: "bill_collector", label: "Cash Collector", tooltipTitle: "Whether a cash (bill/note) collector is installed on this machine." },
            { key: "cardReader", type: "card_reader", label: "Card Reader", tooltipTitle: "Select whether a card reader is physically installed on this machine." },
            { key: "keypad", type: "keypad", label: "Keypad", tooltipTitle: "Whether a keypad is installed on this machine." },
            { key: "reverseQrScanner", type: "reverse_qr_scanner", label: "Reverse QR Scanner", tooltipTitle: "Whether a reverse QR scanner is installed on this machine." },
            { key: "pointOfSaleMachine", type: "pos_machine", label: "Point of Sale Machine", tooltipTitle: "Whether a POS machine is installed on this machine." },
          ],
        },
        fields: [],
      },
      {
        type: "custom",
        component: "machine-automation",
        componentProps: {
          title: "Machine Automation Settings",
          description:
            "Configure automation thresholds and failure handling for this machine.",
          thresholds: [
            {
              key: "vendFailure",
              label: "Vend Failure Threshold",
              enableName: "vendFailureEnabled",
              tooltipTitle:
                "Threshold of vend failures before action is triggered.",
              fields: [
                {
                  name: "vendFailureTotal",
                  label: "Total",
                  type: "number",
                  placeholder: "0",
                  suffix: "%",
                },
                {
                  name: "vendFailIn",
                  label: "Vend Fail in",
                  type: "number",
                  placeholder: "0",
                },
              ],
            },
            {
              key: "coilFailure",
              label: "Coil Failure Threshold",
              enableName: "coilFailureEnabled",
              tooltipTitle:
                "Number of coil failures before the coil is marked failed.",
              fields: [
                {
                  name: "coilFailureValue",
                  label: "",
                  type: "number",
                  placeholder: "Enter value",
                },
              ],
            },
          ],
          toggles: [
            {
              key: "attemptAllCoils",
              label: "Attempt all available coils before marking vend as failed",
              tooltipTitle:
                "When enabled, all available coils are attempted before a vend is marked failed.",
            },
          ],
        },
        fields: [],
      },
    ],
  },

  "display-content": {
    id: "display_content",
    title: "Display & Content",
    // Selecting Display Type = "No Display" in Hardware & Machine Setup means
    // the machine has no customer screen, so all display/content settings are
    // irrelevant — grey the whole section out (and skip its validation on save).
    disableAllWhen: { field: "displayType", equalsName: "No Display" },
    formSections: [
      {
        title: "Network Board & Display",
        icon: <SettingsIcon isActive={true} />,
        description:
          "Configure the digital display board, branding, and on-screen appearance.",
        fields: [
          {
            name: "uploadLogo",
            type: "image",
            label: "Upload Logo",
            tooltipTitle:
              "Logo shown on the machine's customer screen header. Max 2MB (JPG, PNG, JPEG).",
          },
          {
            name: "nameOfDisplay",
            type: "text",
            label: "Name of Display",
            placeholder: "Enter display name",
            tooltipTitle: "Title shown in the display header.",
          },
          {
            name: "sizeOfDisplay",
            type: "text",
            label: "Size of Display",
            placeholder: "In inches",
            tooltipTitle: "Physical size of the display (in inches).",
          },
          {
            name: "primaryColor",
            type: "color",
            label: "Select Primary Color",
            defaultValue: "#E31E24",
            placeholder: "#E31E24",
            tooltipTitle:
              "Primary brand color used for the header, buttons, and accents.",
          },
          {
            name: "secondaryColor",
            type: "color",
            label: "Select Secondary Color",
            defaultValue: "#000000",
            placeholder: "#000000",
            tooltipTitle: "Secondary color used for supporting UI elements.",
          },
          {
            name: "expiryType",
            type: "dropdown",
            label: "Select Expiry Type",
            placeholder: "Select expiry type",
            tooltipTitle: "How product expiry is shown to the customer.",
          },
          {
            name: "blockedCoilDisplay",
            type: "dropdown",
            label: "Blocked Coil Display",
            placeholder: "Select display option",
            tooltipTitle: "How products on blocked coils are displayed.",
          },
          {
            name: "expiredProductDisplay",
            type: "dropdown",
            label: "Expired Product Display",
            placeholder: "Select display option",
            tooltipTitle: "How expired products are displayed.",
          },
          {
            name: "outOfStockProductDisplay",
            type: "dropdown",
            label: "Out of Stock Product Display",
            placeholder: "Select display option",
            tooltipTitle: "How out-of-stock products are displayed.",
          },
          {
            name: "buildVariant",
            type: "dropdown",
            label: "Build Variant",
            placeholder: "Universal (Default)",
            tooltipTitle:
              "Display app build variant. Universal applies to most machines.",
          },
          {
            name: "supportPhoneNumber",
            type: "text",
            label: "Support Phone Number",
            required: true,
            placeholder: "Enter support number",
            tooltipTitle: "Support phone number shown on the display.",
          },
          {
            name: "supportEmail",
            type: "text",
            label: "Support Email",
            required: true,
            placeholder: "Enter support email",
            tooltipTitle: "Support email shown on the display.",
          },
          {
            name: "scrollingText",
            type: "text",
            label: "Scrolling Text",
            required: true,
            fullWidth: true,
            placeholder: "e.g. Welcome! Fresh snacks available daily.",
            tooltipTitle: "Marquee text shown on the customer home screen.",
          },
          {
            name: "useExternalDisplay",
            type: "toggle",
            label: "Use External Display",
            fullWidth: true,
            toggleAlign: "right",
            tooltipTitle:
              "Drive a secondary/external display attached to this machine.",
          },
        ],
      },
      {
        title: "Display & UX Settings",
        icon: <MapIcon isActive={true} />,
        description:
          "Controls what customers see on screen — layout, views, sorting, and advertisement behavior.",
        fields: [
          {
            name: "defaultHomeScreen",
            type: "dropdown",
            label: "Default Home Screen",
            placeholder: "Stock (Default)",
            tooltipTitle: "The screen customers see first on the machine.",
          },
          {
            name: "allowedViews",
            type: "dropdown",
            multi_select: true,
            label: "Allowed Views",
            placeholder: "Select views",
            disabledWhenName: {
              field: "defaultHomeScreen",
              equals: "Fixed Screen",
            },
            tooltipTitle: "Stock-screen layouts customers can switch between.",
          },
          {
            name: "defaultStockView",
            type: "dropdown",
            label: "Default Stock View",
            placeholder: "Select default view",
            disabledWhenName: {
              field: "defaultHomeScreen",
              equals: "Fixed Screen",
            },
            tooltipTitle: "The stock-screen layout shown by default.",
          },
          {
            name: "productsPerRow",
            type: "dropdown",
            label: "Products Per Row",
            placeholder: "3",
            disabledWhenName: {
              field: "defaultHomeScreen",
              equals: "Fixed Screen",
            },
            tooltipTitle: "Number of products shown per row in the grid view.",
          },
          {
            name: "stockSorting",
            type: "dropdown",
            label: "Stock Sorting",
            placeholder: "Select sorting options...",
            disabledWhenName: {
              field: "defaultHomeScreen",
              equals: "Fixed Screen",
            },
            tooltipTitle: "Default sort order of products on the stock screen.",
          },
          {
            name: "language",
            type: "dropdown",
            label: "Language",
            placeholder: "Select language",
            disabledWhenName: {
              field: "defaultHomeScreen",
              equals: "Fixed Screen",
            },
            tooltipTitle: "Display language (locale) used on the machine.",
          },
          {
            name: "currency",
            type: "dropdown",
            label: "Currency",
            placeholder: "Select currency",
            tooltipTitle: "Currency shown on the machine (symbol sent to the device).",
          },
          // Country dropdown hidden for now (country_code still stored, default
          // "IN"). Re-add a { name: "country", type: "dropdown", label:
          // "Country" } field here to surface it again.
          {
            name: "showLabels",
            type: "toggle",
            label: "Show Labels",
            fullWidth: true,
            toggleAlign: "right",
            disabledWhenName: {
              field: "defaultHomeScreen",
              equals: "Fixed Screen",
            },
            tooltipTitle: "Show product name and price labels on the screen.",
          },
          {
            name: "showVirtualKeyboard",
            type: "toggle",
            label: "Show Virtual Keyboard",
            fullWidth: true,
            toggleAlign: "right",
            disabledWhenName: {
              field: "defaultHomeScreen",
              equals: "Fixed Screen",
            },
            tooltipTitle: "Show an on-screen keyboard for customer input.",
          },
        ],
      },
      {
        title: "Advertisement Settings",
        icon: <DatabaseIcon isActive={true} />,
        description:
          "Configure advertisement content shown on the machine's customer screen.",
        fields: [
          {
            name: "fixedScreenAdvertisement",
            type: "toggle",
            label: "Fixed Screen Advertisement on Home Screen",
            fullWidth: true,
            toggleAlign: "right",
            tooltipTitle:
              "Show a fixed full-screen advertisement on the home screen.",
          },
          {
            name: "cartScreenAdUrl",
            type: "text",
            label: "Cart Screen Ad URL",
            fullWidth: true,
            placeholder: "https://",
            tooltipTitle: "URL of the advertisement shown on the cart screen.",
          },
          {
            name: "cartScreenBannerUrl",
            type: "text",
            label: "Cart Screen Banner URL",
            fullWidth: true,
            placeholder: "https://",
            tooltipTitle: "URL of the banner shown on the cart screen.",
          },
        ],
      },
    ],
  },

  // Internal key kept as "gst-compliance" (used by save-case, validation rules
  // and section status); only the displayed title/label is "Payments & Business".
  "gst-compliance": {
    id: "gst_and_compliance",
    title: "Payments & Business",
    formSections: [
      {
        title: "GST Details",
        icon: <FileCheckIcon isActive={true} />,
        description:
          "GST number and company name entered here will appear on all invoices generated for this machine.",
        fields: [
          {
            name: "gstNumber",
            label: "GST Number",
            required: true,
            placeholder: "Enter GST Number",
            value: "",
            tooltipTitle:
              "Enter the GST identification number (max 15 characters).",
          },
          {
            name: "gstCompanyName",
            label: "GST Company Name",
            required: true,
            placeholder: "Enter GST Company Name",
            value: "",
            tooltipTitle: "Enter the registered company name for GST.",
          },
        ],
      },
      {
        title: "Payment Configuration",
        icon: <SettingsIcon isActive={true} />,
        description:
          "Define which payment methods are available on this machine and how they are presented.",
        fields: [
          {
            name: "enabledPaymentMethods",
            label: "Enabled Payment Methods",
            type: "dropdown",
            multi_select: true,
            required: true,
            fullWidth: true,
            placeholder: "Select payment methods...",
            tooltipTitle:
              "Payment methods available to customers on this machine.",
            options: [],
          },
          {
            // Options are constrained at runtime to the Enabled Payment Methods
            // currently selected (see ConfigSection).
            name: "defaultPaymentMethod",
            label: "Default Payment Method",
            type: "dropdown",
            required: true,
            fullWidth: true,
            placeholder: "Select default method",
            tooltipTitle:
              "The method selected by default. Must be one of the enabled methods above.",
            options: [],
          },
          {
            // POS Brand — only shown when "POS" is an enabled payment method.
            // Linked to the Point of Sale Machine peripheral (Hardware & Machine
            // Setup): it reads/writes formData.peripherals.pointOfSaleMachine, so
            // it can be set from either section. Brand options come from
            // peripheralOptions.brands.pos_machine. Handled specially in
            // ConfigSection (type "payment-peripheral-brand").
            name: "posBrand",
            label: "POS Brand",
            type: "payment-peripheral-brand",
            fullWidth: true,
            placeholder: "Select POS brand",
            tooltipTitle:
              "POS machine brand. Linked to the Point of Sale Machine peripheral in Hardware & Machine Setup — setting it here also configures it there.",
            paymentMethod: "POS",
            peripheralKey: "pointOfSaleMachine",
            peripheralType: "pos_machine",
          },
        ],
      },
      {
        // Third Party Ordering Channels — only shown when "Third Party Orders"
        // is an enabled payment method (visibility handled in ConfigSection).
        // Lists the company's customer platforms (grouped by code); the user
        // selects which platform instances this machine accepts orders from.
        title: "Third Party Ordering Channels",
        type: "custom",
        component: "third-party-channels",
        showWhenPaymentMethod: "Third Party Orders",
        componentProps: {
          title: "Third Party Ordering Channels",
          description:
            "Select which third party ordering platforms this machine accepts orders from.",
        },
        fields: [],
      },
      {
        title: "Pipeline",
        icon: <DatabaseIcon isActive={true} />,
        description:
          "Configure pipeline connectivity for automated stock replenishment or data synchronisation.",
        fields: [
          {
            name: "pipeline",
            type: "toggle",
            label: "Pipeline",
            fullWidth: true,
            tooltipTitle:
              "Enable pipeline connectivity for automated stock replenishment or data synchronisation.",
          },
        ],
      },
    ],
  },

  "action-history": {
    id: "action-history",
    title: "Action History",
    formSections: [
      {
        title: "History Settings",
        icon: <LocationIcon isActive={true} />,
        type: "custom",
        component: "action-history",
        fields: [],
      },
    ],
  },
};

export const SECTION_VALIDATION_RULES = {
  "location-client": {
    // Required fields
    region: { required: true },
    state: { required: true },
    city: { required: true },

    latitude: {
      decimalNumber: true,
      startDecimalVal: -90,
      endDecimalVal: 90,
      errorMessage: "Latitude must be between -90 and 90",
    },
    longitude: {
      decimalNumber: true,
      startDecimalVal: -180,
      endDecimalVal: 180,
      errorMessage: "Longitude must be between -180 and 180",
    },
    buildingLatitude: {
      decimalNumber: true,
      startDecimalVal: -90,
      endDecimalVal: 90,
      errorMessage: "Building Latitude must be between -90 and 90",
    },
    buildingLongitude: {
      decimalNumber: true,
      startDecimalVal: -180,
      endDecimalVal: 180,
      errorMessage: "Building Longitude must be between -180 and 180",
    },
    buildingToMachineTime: {
      number: true,
      minVal: 0,
      maxVal: 999,
      errorMessage: "Building to Machine Time must be between 0 and 999",
    },
  },

  "machine-identity": {
    machineDisplayName: {
      minVal: 2,
      maxVal: 100,
      required: true,
    },
    machineExternalId: {
      minVal: 2,
      maxVal: 100,
      required: true,
    },
  },

  "hardware-setup": {
    defaultTemperature: {
      number: true,
      minVal: -40,
      maxVal: 99,
      errorMessage: "Default Temperature must be between -40 and 99",
    },
    vendFailureTotal: {
      number: true,
      minVal: 0,
      maxVal: 100,
      errorMessage: "Vend Failure Total must be between 0 and 100",
    },
    vendFailIn: {
      number: true,
      minVal: 0,
      maxVal: 99999,
      errorMessage: "Vend Fail in must be between 0 and 99999",
    },
    coilFailureValue: {
      number: true,
      minVal: 0,
      maxVal: 99999,
      errorMessage: "Coil Failure Threshold must be between 0 and 99999",
    },
  },

  "customer-experience": {
    screenInactivityTimeout: {
      number: true,
      minVal: 0,
      maxVal: 99999,
      errorMessage: "Screen Inactivity Timeout must be between 0 and 99999",
    },
    cartResetTimeout: {
      number: true,
      minVal: 0,
      maxVal: 99999,
      errorMessage: "Cart Reset Timeout must be between 0 and 99999",
    },
    paymentTimeout: {
      number: true,
      minVal: 0,
      maxVal: 99999,
      errorMessage: "Payment Timeout must be between 0 and 99999",
    },
    postDispenseTimeout: {
      number: true,
      minVal: 0,
      maxVal: 99999,
      errorMessage: "Post Dispense Timeout must be between 0 and 99999",
    },
    maxItemsInCart: {
      number: true,
      minVal: 0,
      maxVal: 1000,
      errorMessage: "Max Items in Cart must be between 0 and 1000",
    },
    maxUniqueItemsPerCart: {
      number: true,
      minVal: 0,
      maxVal: 1000,
      errorMessage: "Max Unique Items Per Cart must be between 0 and 1000",
    },
    maxCartAmount: {
      number: true,
      minVal: 0,
      maxVal: 1000000,
      errorMessage: "Max Cart Amount must be between 0 and 1000000",
    },
    unlockTime: {
      number: true,
      minVal: 0,
      maxVal: 99999,
      errorMessage: "Unlock Time must be between 0 and 99999",
    },
  },

  "ownership-mapping": {
    machineOwnedBy: { required: true },
    managedBy: { required: true },
    maintenanceStartDate: { required_with: "maintenanceEndDate" },
    maintenanceEndDate: {
      required_with: "maintenanceStartDate",
      after: "maintenanceStartDate",
    },
    insuranceStartDate: { required_with: "insuranceEndDate" },
    insuranceEndDate: {
      required_with: "insuranceStartDate",
      after: "insuranceStartDate",
    },
    ownershipStartDate: { required_with: "ownershipEndDate" },
    ownershipEndDate: {
      required_with: "ownershipStartDate",
      after: "ownershipStartDate",
    },
  },

  "installation-contract": {
    installationDate: { required: true },
    contractStartDate: { required_with: "contractEndDate" },
    contractEndDate: {
      required_with: "contractStartDate",
      after: "contractStartDate",
    },
    // contractEndDate: { required: true },
  },

  "refill-configuration": {
    defaultWarehouse: { required: true },
    // defaultRefillPartner: { required: true },
    refillingOwnership: { required: true },

    // Number validations
    gracePeriodMinutes: {
      number: true,
      minVal: 0,
      maxVal: 999,
      errorMessage: "Grace period must be between 0 and 999 minutes",
    },
    bufferPeriodMinutes: {
      number: true,
      minVal: 0,
      maxVal: 999,
      errorMessage: "Buffer period must be between 0 and 999 minutes",
    },
    machineToMachineTimeMinutes: {
      number: true,
      minVal: 0,
      maxVal: 999,
      errorMessage: "Machine to machine time must be between 0 and 999 minutes",
    },
    refillSchedule: {},
  },

  "machine-uptime": {
    // uptime_timezone: { required: true },
    uptimeType: { required: true },
  },

  "gst-compliance": {
    gstNumber: {
      required: true,
      // pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
      errorMessage: "Please enter a valid GST number",
    },
    gstCompanyName: {
      required: true,
      minLength: 2,
      maxLength: 200,
    },
    // Default Payment Method is sourced from Enabled Payment Methods, so
    // requiring it transitively requires at least one enabled method.
    defaultPaymentMethod: {
      required: true,
    },
  },

  "display-content": {
    supportPhoneNumber: {
      required: true,
      maxLength: 20,
    },
    supportEmail: {
      required: true,
      email: true,
    },
    scrollingText: {
      required: true,
      maxLength: 250,
    },
  },

  "action-history": {},
};

export const FIELD_NAME_MAP = {
  region: "Region",
  state: "State",
  city: "City",
  latitude: "Latitude",
  longitude: "Longitude",
  buildingLatitude: "Building Latitude",
  buildingLongitude: "Building Longitude",
  buildingToMachineTime: "Building to Machine Time",
  machineDisplayName: "Machine Display Name",
  machineExternalId: "Machine External Id",
  machineOwnedBy: "Machine Owned By",
  managedBy: "Managed By",
  installationDate: "Installation Date",
  contractStartDate: "Contract Start Date",
  contractEndDate: "Contract End Date",
  installationDate: "Installation Date",
  defaultWarehouse: "Default Warehouse",
  defaultRefillPartner: "Default Refill Partner",
  refillingOwnership: "Refilling Ownership",
  gracePeriodMinutes: "Grace Period",
  bufferPeriodMinutes: "Buffer Period",
  machineToMachineTimeMinutes: "Machine to Machine Time",
  uptime_timezone: "Timezone",
  uptimeType: "Uptime Type",
  gstNumber: "GST Number",
  gstCompanyName: "GST Company Name",
  refillSchedule: "",
  backupWarehouses: "Backup Warehouses",
  backupRefillPartners: "Backup Refill Partners",
  defaultTechnician: "Default Technician"

};

export const DAY_CODE_MAP = {
  MON: "Monday",
  TUE: "Tuesday",
  WED: "Wednesday",
  THU: "Thursday",
  FRI: "Friday",
  SAT: "Saturday",
  SUN: "Sunday",
};
